import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import DashboardLayout, { Product as D } from '../components/DashboardLayout';
import PaymentConfirmModal from '../components/PaymentConfirmModal';
import PaymentMethodFeeCompare from '../components/PaymentMethodFeeCompare';
import EmailVerificationPrompt from '../components/EmailVerificationPrompt';
import Timeline from '../components/Timeline';
import TransactionJourney from '../components/TransactionJourney';
import { TransactionStateSummary } from '../components/TransactionExperience';
import MoneyBreakdown from '../components/MoneyBreakdown';
import { paymentRecord, providerFeeSplit } from '../components/paymentRecord';
import FullCostQuote, { payableMethods } from '../components/FullCostQuote';
import TransactionShareActions from '../components/TransactionShareActions';
import VerificationBadges from '../components/VerificationBadges';
import { publicTransactionUrl, safeExternalHttpUrl } from '../utils/transactionSharing';
import TransactionActivityFeed from '../components/TransactionActivityFeed';
import { TransactionTimeline } from '../components/TransactionTimeline';
import AutoReleaseCountdown from '../components/AutoReleaseCountdown';
import SecureImage from '../components/SecureImage';
import {
  getPaymentDisplayAmounts,
  getSellerPayoutDisplayAmount,
  getTransactionActionAvailability,
  getTransactionFlowType,
  getPayoutStageCopy,
  isCourierAutoReleaseEnabled,
  hasAuthoritativeReleaseEvidence,
  confirmedSettlementAt,
  hasAuthoritativeSettlementEvidence,
  isInPersonTransaction,
  isProviderFundingConfirmed,
  mapEscrowUiStateToTimelineState,
  resolveEscrowUiState,
} from '../components/transactionState';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { buildTransactionActivity } from '../utils/transactionActivity';
import { resolveTransactionPlatformFee, usePlatformConfig } from '../context/PlatformConfigContext';
import { getPayoutScheduleMessage, getPostReleaseMessage, calculatePayoutSchedule } from '../utils/payoutSchedule';
import api, { API_BASE_URL } from '../utils/api';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, User, Download, CheckCircle2, Image as ImageIcon,
  Star, Copy, Check, AlertTriangle, CreditCard, Truck, Shield,
  Loader2, Phone, Lock, RefreshCw, Clock, Banknote
} from 'lucide-react';

const API = API_BASE_URL;
const PHONE_VERIFICATION_PROMPT = 'Verify your phone number to continue.';
const BANKING_DETAILS_PROMPT = 'Add banking details to receive payouts.';
// Launch default is OFF — Courier Guy has not completed real end-to-end
// certification yet. Absence of the env var must NOT silently enable it.
const COURIER_ENABLED = process.env.REACT_APP_COURIER_ENABLED === 'true';

function roundMoney(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function splitTrustTradeFee(totalFee, feeAllocation) {
  const fee = roundMoney(totalFee);
  const alloc = (feeAllocation || 'BUYER').toUpperCase();

  if (['SELLER', 'SELLER_AGENT'].includes(alloc)) {
    return { buyerFee: 0, sellerFee: fee };
  }
  if (['BUYER_SELLER', 'SPLIT', 'SPLIT_AGENT', 'BUYER_SELLER_AGENT'].includes(alloc)) {
    const buyerFee = roundMoney(fee / 2);
    return { buyerFee, sellerFee: roundMoney(fee - buyerFee) };
  }
  return { buyerFee: fee, sellerFee: 0 };
}

// TrustTrade does NOT know the real payment-provider processing fee before the
// buyer actually pays: confirmed directly against TradeSafe's API — the
// checkoutLink mutation returns only a URL, and no deposit (where the real
// processingFee eventually appears) exists until a payment is attempted. So
// this screen never computes or displays a guessed rand amount for it — only
// WHETHER a given side carries any provider processing cost under the current
// fee_allocation (mirrors splitTrustTradeFee's allocation buckets), which is
// a fact we do know without guessing its magnitude.
function tsFeeApplies(feeAllocation, side) {
  const alloc = (feeAllocation || 'BUYER').toUpperCase();
  if (['SELLER', 'SELLER_AGENT'].includes(alloc)) return side === 'seller';
  if (['BUYER_SELLER', 'SPLIT', 'SPLIT_AGENT', 'BUYER_SELLER_AGENT'].includes(alloc)) return true;
  return side === 'buyer';
}

function parseErrorMessage(error) {
  const detail = error.response?.data?.detail;
  if (!detail) return 'An error occurred';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
  return 'An error occurred';
}

function formatDetailDate(value) {
  if (!value) return 'Recorded by TrustTrade';
  return new Date(value).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function addBusinessDays(date, days) {
  const next = new Date(date);
  let remaining = days;
  while (remaining > 0) {
    next.setDate(next.getDate() + 1);
    const day = next.getDay();
    if (day !== 0 && day !== 6) remaining -= 1;
  }
  return next;
}

function formatSettlementDate(value) {
  return new Date(value).toLocaleDateString('en-ZA', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
  });
}

function getPayoutActivity(transaction) {
  const releasedAt = hasAuthoritativeReleaseEvidence(transaction)
    ? (transaction.released_at || transaction.funds_released_at || null)
    : null;
  const processingStartedAt = transaction.payout_processing_started_at ||
    transaction.withdrawal_requested_at ||
    transaction.withdrawal_started_at ||
    transaction.withdrawal_triggered_at ||
    releasedAt;
  const settlementConfirmed = hasAuthoritativeSettlementEvidence(transaction);
  const settlementConfirmedAt = confirmedSettlementAt(transaction);

  return {
    releasedAt,
    processingStartedAt,
    settlementConfirmedAt,
    settlementConfirmed,
  };
}

function getPayoutStatus(transaction) {
  const activity = getPayoutActivity(transaction);
  const payout = getPayoutStageCopy(transaction);
  const releaseDate = activity.releasedAt ? new Date(activity.releasedAt) : new Date();
  const isWeekend = releaseDate.getDay() === 0 || releaseDate.getDay() === 6;

  if (activity.settlementConfirmedAt && payout.stage === 'paid') {
    return { label: 'Completed', tone: 'green' };
  }
  if (['failed', 'unknown'].includes(payout.stage)) {
    return { label: payout.label, tone: 'amber' };
  }
  if (isWeekend) {
    return { label: 'Expected next business day', tone: 'amber' };
  }
  if (activity.processingStartedAt || ['initiated', 'processing'].includes(payout.stage)) {
    return { label: 'Bank processing', tone: 'blue' };
  }
  return { label: 'Processing', tone: 'slate' };
}

function getSettlementEstimate(transaction) {
  const activity = getPayoutActivity(transaction);
  if (activity.settlementConfirmedAt) {
    return {
      title: 'Settlement confirmed',
      detail: formatDetailDate(activity.settlementConfirmedAt),
    };
  }

  const basis = activity.processingStartedAt || activity.releasedAt || new Date();
  const releasedDate = new Date(basis);
  const isWeekend = releasedDate.getDay() === 0 || releasedDate.getDay() === 6;
  const estimatedDate = addBusinessDays(releasedDate, 1);
  const estimatedDayEnd = new Date(estimatedDate);
  estimatedDayEnd.setHours(23, 59, 59, 999);

  if (new Date() > estimatedDayEnd) {
    return {
      title: 'Settlement status',
      detail: 'Bank settlement is pending confirmation.',
    };
  }

  return {
    title: isWeekend ? 'Weekend release notice' : 'Expected settlement estimate',
    detail: isWeekend
      ? `Bank processing resumes on the next business day. Current estimate: ${formatSettlementDate(estimatedDate)}.`
      : `Expected next business day: ${formatSettlementDate(estimatedDate)}.`,
    holidayNote: 'Public holiday handling will follow bank processing calendars.',
  };
}

function PayoutStatusBadge({ status }) {
  const tones = {
    green: { bg: 'rgba(16,185,129,0.14)', text: '#3DD194', border: 'rgba(16,185,129,0.30)' },
    blue: { bg: 'rgba(59,130,246,0.14)', text: '#2F9BFF', border: 'rgba(59,130,246,0.30)' },
    amber: { bg: 'rgba(245,158,11,0.14)', text: '#F5AB1F', border: 'rgba(245,158,11,0.30)' },
    slate: { bg: '#242830', text: '#9AA4B2', border: '#242830' },
  };
  const tone = tones[status.tone] || tones.slate;

  return (
    <span
      title={status.label === 'Completed' ? 'Bank settlement is confirmed.' : 'Funds have been released and are awaiting bank clearing.'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 999,
        padding: '5px 9px',
        background: tone.bg,
        color: tone.text,
        border: `1px solid ${tone.border}`,
        fontSize: 11,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {status.label}
    </span>
  );
}

function FinalizedEscrowState({ transaction, uiState }) {
  const activity = getPayoutActivity(transaction);
  const status = getPayoutStatus(transaction);
  const estimate = getSettlementEstimate(transaction);
  const settled = uiState.state === 'COMPLETED' && Boolean(activity.settlementConfirmedAt);
  const completedAt = activity.settlementConfirmedAt || activity.releasedAt || transaction.updated_at;

  return (
    <div style={{
      background: 'linear-gradient(135deg,rgba(22,163,74,0.08),#16181B)',
      border: '1px solid rgba(16,185,129,0.30)',
      borderLeft: '3px solid #3DD194',
      borderRadius: 14,
      padding: '20px 22px',
      boxShadow: '0 12px 34px rgba(16,185,129,0.1)',
    }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(16,185,129,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <CheckCircle2 size={22} color="#3DD194" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 5 }}>
            <p style={{ fontSize: 15, fontWeight: 800, color: '#3DD194', margin: 0 }}>
              {settled ? 'Payment complete' : 'Payment Released'}
            </p>
            <PayoutStatusBadge status={status} />
          </div>
          <p style={{ fontSize: 13, color: '#3DD194', margin: '0 0 14px', lineHeight: 1.6 }}>
            {settled ? 'Payment release and seller bank settlement are confirmed.' : 'Payment released. Bank settlement is pending confirmation.'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
            {[
              ['Payment state', uiState.label],
              ['Payout status', status.label],
              [settled ? 'Settlement confirmation' : 'Settlement estimate', estimate.detail],
              ['Latest confirmed event', formatDetailDate(completedAt)],
              ['Payment provider', 'Third-party provider'],
            ].map(([label, value]) => (
              <div key={label} style={{ background: 'rgba(22,163,74,0.05)', border: '1px solid rgba(16,185,129,0.14)', borderRadius: 10, padding: '10px 12px' }}>
                <p style={{ fontSize: 10, color: '#3DD194', fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</p>
                <p style={{ fontSize: 12, color: '#3DD194', fontWeight: 700, margin: 0 }}>{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PayoutTimeline({ transaction }) {
  const activity = getPayoutActivity(transaction);
  const status = getPayoutStatus(transaction);
  const estimate = getSettlementEstimate(transaction);
  const steps = [
    { label: 'Payment released', value: activity.releasedAt ? formatDetailDate(activity.releasedAt) : 'Waiting for payment release', state: activity.releasedAt ? 'complete' : 'pending' },
    { label: 'Payout processing started', value: activity.processingStartedAt ? formatDetailDate(activity.processingStartedAt) : 'Starts after release', state: activity.processingStartedAt ? 'complete' : activity.releasedAt ? 'active' : 'pending' },
    { label: 'Bank processing', value: activity.settlementConfirmedAt ? 'Bank settlement confirmed' : activity.releasedAt ? 'Bank settlement pending confirmation' : 'Funds are moving through banking rails', state: activity.settlementConfirmedAt ? 'complete' : activity.releasedAt ? 'active' : 'pending' },
    { label: estimate.title, value: `${estimate.detail} ${estimate.holidayNote || ''}`.trim(), state: activity.releasedAt ? 'active' : 'pending' },
    { label: 'Completed', value: activity.settlementConfirmedAt ? formatDetailDate(activity.settlementConfirmedAt) : 'No confirmed bank settlement yet', state: activity.settlementConfirmedAt ? 'complete' : 'pending' },
  ];
  const activityRows = [
    ['Payment released', activity.releasedAt],
    ['Processing started', activity.processingStartedAt],
    ['Settlement confirmed', activity.settlementConfirmedAt],
  ];

  return (
    <div style={{ background: '#16181B', border: '1px solid #242830', borderRadius: 14, padding: '18px 20px', boxShadow: '0 10px 28px rgba(15,30,53,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <p style={{ fontSize: 11, color: '#9AA4B2', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>Payout timeline</p>
          <p style={{ fontSize: 13, color: '#9AA4B2', margin: 0 }}>
            {activity.settlementConfirmedAt ? 'Bank settlement is confirmed. The recorded events are shown below.' : 'Payment released. Bank settlement is pending confirmation.'}
          </p>
        </div>
        <PayoutStatusBadge status={status} />
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        {steps.map((step) => (
          <div key={step.label} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 10, alignItems: 'start' }}>
            <div style={{ width: 14, height: 14, borderRadius: '50%', marginTop: 2, background: step.state === 'complete' ? '#3DD194' : step.state === 'active' ? '#2F9BFF' : '#242830', boxShadow: step.state === 'complete' ? '0 0 0 4px rgba(16,185,129,0.12)' : step.state === 'active' ? '0 0 0 4px rgba(37,99,235,0.12)' : 'none' }} />
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#F5F7FA', margin: 0 }}>{step.label}</p>
              <p style={{ fontSize: 12, color: '#9AA4B2', margin: '2px 0 0' }}>{step.value}</p>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #242830' }}>
        <p style={{ fontSize: 11, color: '#9AA4B2', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>
          Payout activity timestamps
        </p>
        <div style={{ display: 'grid', gap: 7 }}>
          {activityRows.map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
              <span style={{ color: '#9AA4B2', fontFamily: 'monospace' }}>{label}</span>
              <span style={{ color: value ? '#F5F7FA' : '#9AA4B2', fontWeight: 600, textAlign: 'right' }}>
                {value ? formatDetailDate(value) : 'Not recorded yet'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function getSADateKey(date) {
  const d = new Date(date.getTime() + 2 * 60 * 60 * 1000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function PayoutTimelineTracker({ transaction, platformConfig }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const activity = getPayoutActivity(transaction);
  const releasedAt = activity.releasedAt ? new Date(activity.releasedAt) : null;
  if (!releasedAt) return null;

  const { payoutRunAt, bankRunLabel } = calculatePayoutSchedule(releasedAt, platformConfig || {});
  const settlementConfirmed = activity.settlementConfirmed;

  const inBankProcessing = now >= payoutRunAt && !settlementConfirmed;
  const completed = settlementConfirmed;

  // Progress: 0→80% during payout processing (pre-bank-run), 80→95% during bank processing, 100% complete
  const totalMs = payoutRunAt.getTime() - releasedAt.getTime();
  const elapsedMs = Math.min(now.getTime() - releasedAt.getTime(), totalMs);
  const rawProgress = totalMs > 0 ? Math.round((elapsedMs / totalMs) * 80) : 0;
  const progress = completed ? 100 : inBankProcessing ? 92 : Math.min(78, rawProgress);

  // Countdown
  const msUntilPayout = Math.max(0, payoutRunAt.getTime() - now.getTime());
  const hoursUntilPayout = Math.floor(msUntilPayout / (1000 * 60 * 60));
  const minutesUntilPayout = Math.floor((msUntilPayout % (1000 * 60 * 60)) / (1000 * 60));

  const countdownText = completed
    ? 'Payout complete'
    : msUntilPayout === 0
    ? 'Bank processing now'
    : hoursUntilPayout > 0
    ? `Estimated payout in: ${hoursUntilPayout}h ${minutesUntilPayout}m`
    : minutesUntilPayout > 0
    ? `Estimated payout in: ${minutesUntilPayout}m`
    : 'Processing now';

  // Date label helpers (SA timezone = UTC+2, no DST)
  const relDayLabel = (target) => {
    const tk = getSADateKey(target);
    const nk = getSADateKey(now);
    if (tk === nk) return 'Today';
    const tmr = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    if (tk === getSADateKey(tmr)) return 'Tomorrow';
    const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const utcDay = new Date(target.getTime() + 2 * 60 * 60 * 1000).getUTCDay();
    return DAYS[utcDay];
  };

  // Payout time string from the UTC payoutRunAt (which was built in SAST)
  const payoutH = String(new Date(payoutRunAt.getTime() + 2 * 60 * 60 * 1000).getUTCHours()).padStart(2, '0');
  const payoutM = String(payoutRunAt.getUTCMinutes()).padStart(2, '0');
  const payoutTimeStr = `${payoutH}:${payoutM}`;

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const saPayoutDate = new Date(payoutRunAt.getTime() + 2 * 60 * 60 * 1000);
  const payoutDay = saPayoutDate.getUTCDate();
  const payoutMonth = MONTHS[saPayoutDate.getUTCMonth()];
  const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const payoutWeekday = WEEKDAYS[saPayoutDate.getUTCDay()];

  const payoutBoldLabel = `${payoutWeekday} ${payoutDay} ${payoutMonth} at ${payoutTimeStr}`;
  const inspectionEndLabel = `${relDayLabel(payoutRunAt)} ${payoutTimeStr}`;

  // Once a release has been confirmed, the timeline must never read as an
  // active/future buyer review or inspection window — that period is over
  // the moment payment is released (TT-296674, 2026-10-13: the old step
  // labelled the still-open buyer review window, with a countdown to its
  // end, well after release had already happened). "Payout Processing"
  // covers release -> next bank run; "Bank Settlement" covers the bank run
  // itself through confirmed settlement — payment release and bank
  // settlement stay distinct steps.
  const steps = [
    {
      label: 'Payment Released',
      detail: formatDetailDate(activity.releasedAt),
      state: 'complete',
    },
    {
      label: 'Payout Processing',
      detail: completed
        ? 'Processing complete'
        : inBankProcessing
        ? `Bank run in progress — estimated ${bankRunLabel}`
        : `Next bank run: ${inspectionEndLabel}`,
      state: completed ? 'complete' : 'active',
    },
    {
      label: 'Bank Settlement',
      detail: completed ? 'Bank settlement confirmed' : payoutBoldLabel,
      state: completed ? 'complete' : inBankProcessing ? 'active' : 'pending',
      highlight: !completed,
    },
  ];

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(16,185,129,0.14) 0%, #16181B 100%)',
      border: '1px solid rgba(16,185,129,0.30)',
      borderLeft: '3px solid #3DD194',
      borderRadius: 14,
      padding: '20px 22px',
      boxShadow: '0 4px 20px rgba(16,185,129,0.08)',
    }}>
      <style>{`@keyframes payout-pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <p style={{ fontSize: 11, color: '#3DD194', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 3px' }}>
            Payout Timeline
          </p>
          <p style={{ fontSize: 15, fontWeight: 700, color: '#F5F7FA', margin: 0 }}>
            {countdownText}
          </p>
        </div>
        {!completed && msUntilPayout > 0 && (
          <div style={{ background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.30)', borderRadius: 99, padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
            <Clock size={11} color="#3DD194" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#3DD194', whiteSpace: 'nowrap' }}>
              {hoursUntilPayout > 0 ? `${hoursUntilPayout}h ${minutesUntilPayout}m` : `${minutesUntilPayout}m`}
            </span>
          </div>
        )}
        {completed && (
          <div style={{ background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.30)', borderRadius: 99, padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 5 }}>
            <CheckCircle2 size={11} color="#3DD194" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#3DD194' }}>Done</span>
          </div>
        )}
      </div>

      {/* Bold estimated payout date */}
      {!completed && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.30)', borderRadius: 8, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Banknote size={14} color="#3DD194" style={{ flexShrink: 0 }} />
            <div>
              <span style={{ fontSize: 12, color: '#3DD194' }}>Estimated payout: </span>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#3DD194' }}>{payoutBoldLabel}</span>
            </div>
          </div>
          <p style={{ fontSize: 11, color: '#9AA4B2', margin: '6px 2px 0', lineHeight: 1.4 }}>
            Estimates based on standard processing. Actual timing may vary by 1–2 hours.
          </p>
        </div>
      )}

      {/* Progress bar */}
      <div style={{ background: '#242830', borderRadius: 99, height: 5, marginBottom: 18, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          background: completed ? '#3DD194' : 'linear-gradient(90deg, #3DD194, #34d399)',
          borderRadius: 99,
          width: `${progress}%`,
          transition: 'width 1s ease',
        }} />
      </div>

      {/* Steps */}
      <div>
        {steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18, flexShrink: 0 }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: step.state === 'complete' ? '#3DD194' : step.state === 'active' ? '#2F9BFF' : '#242830',
                boxShadow: step.state === 'active' ? '0 0 0 4px rgba(37,99,235,0.12)' : 'none',
              }}>
                {step.state === 'complete' && <Check size={10} color="#16181B" />}
                {step.state === 'active' && (
                  <Loader2 size={9} color="#16181B" style={{ animation: 'spin 1s linear infinite' }} />
                )}
              </div>
              {i < steps.length - 1 && (
                <div style={{
                  width: 2, flex: 1, minHeight: 14,
                  background: step.state === 'complete' ? 'rgba(16,185,129,0.30)' : '#242830',
                  margin: '3px 0',
                }} />
              )}
            </div>

            <div style={{ flex: 1, paddingBottom: i < steps.length - 1 ? 12 : 0 }}>
              <p style={{
                fontSize: 13,
                fontWeight: step.state !== 'pending' ? 700 : 500,
                color: step.state === 'pending' ? '#9AA4B2' : '#F5F7FA',
                margin: '0 0 2px',
                lineHeight: 1.3,
              }}>
                {step.label}
              </p>
              {step.highlight && step.state !== 'complete' ? (
                <span style={{
                  fontSize: 13, fontWeight: 800, color: '#3DD194',
                  background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.30)',
                  borderRadius: 6, padding: '2px 8px', display: 'inline-block',
                }}>
                  {step.detail}
                </span>
              ) : (
                <p style={{
                  fontSize: 12,
                  color: step.state === 'pending' ? '#242830' : '#9AA4B2',
                  margin: 0, lineHeight: 1.4,
                }}>
                  {step.detail}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Seller-facing preview shown while funds are sitting in escrow (PAYMENT_SECURED)
// but the release hasn't happened yet. Gives the seller a same-page answer to
// "when am I getting paid?" by estimating: earliest release time → bank run via
// calculatePayoutSchedule → date the money lands. All dates labelled "Estimated"
// (not "Expected") with a subtle clarifying note.
function SellerExpectedPayoutCard({ transaction, platformConfig }) {
  const fundsReceivedAt = transaction.funds_received_at
    || transaction.payment_verified_at
    || null;
  if (!fundsReceivedAt) return null;

  // Earliest release timestamp the seller can hope for. Prefer the backend-set
  // auto_release_at; fall back to funds_received_at + delivery-method default if
  // the field hasn't been written yet.
  const deliveryMethod = (transaction.delivery_method || 'courier').toLowerCase();
  const AUTO_RELEASE_DAYS_BY_METHOD = {
    courier: 3,
    postnet: 5,
    bank_deposit: 2,
    digital: 0,
    instant: 0,
    meet_in_person: 1,
    collection: 1,
    other: 3,
  };
  const fallbackDays = AUTO_RELEASE_DAYS_BY_METHOD[deliveryMethod] ?? 3;
  const courierTimerOff = getTransactionFlowType({ delivery_method: deliveryMethod }) === 'delivery'
    && !isCourierAutoReleaseEnabled();
  const estimatedReleaseAt = courierTimerOff
    ? new Date()
    : transaction.auto_release_at
      ? new Date(transaction.auto_release_at)
      : new Date(new Date(fundsReceivedAt).getTime() + fallbackDays * 24 * 60 * 60 * 1000);

  // Walk the estimated release through the bank-run schedule to get the date
  // the money actually lands in the seller's bank.
  const { payoutRunAt, bankRunLabel } = calculatePayoutSchedule(estimatedReleaseAt, platformConfig || {});

  const dateFmt = (d) => new Date(d).toLocaleDateString('en-ZA', {
    weekday: 'long', day: '2-digit', month: 'short', year: 'numeric',
  });

  const earliestReleaseLabel = dateFmt(estimatedReleaseAt);
  const payoutLandsLabel = dateFmt(payoutRunAt);
  const releasePlainEnglish = courierTimerOff
    ? 'when the buyer confirms receipt'
    : deliveryMethod === 'digital'
      ? 'as soon as the buyer confirms (auto-releases shortly otherwise)'
      : 'when the buyer confirms receipt or the auto-release timer expires';

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(16,185,129,0.14) 0%, #16181B 100%)',
      border: '1px solid rgba(16,185,129,0.30)',
      borderLeft: '3px solid #3DD194',
      borderRadius: 14,
      padding: '18px 20px',
      boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <p style={{ fontSize: 11, color: '#3DD194', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 3px' }}>
            Payout Timeline
          </p>
          <p style={{ fontSize: 15, fontWeight: 700, color: '#F5F7FA', margin: 0 }}>
            Funds are protected — here's when to expect your money
          </p>
        </div>
        <div style={{ background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.30)', borderRadius: 99, padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          <Shield size={11} color="#3DD194" />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#3DD194', whiteSpace: 'nowrap' }}>Protected</span>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
        <div style={{ background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.30)', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={14} color="#3DD194" style={{ flexShrink: 0 }} />
            <div>
              <span style={{ fontSize: 12, color: '#3DD194' }}>Estimated funds released: </span>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#3DD194' }}>
                {courierTimerOff ? 'After buyer confirmation' : earliestReleaseLabel}
              </span>
            </div>
          </div>
          <p style={{ fontSize: 11, color: '#9AA4B2', margin: '4px 0 0 22px', lineHeight: 1.4 }}>
            Funds release {releasePlainEnglish}.
          </p>
        </div>

        <div style={{ background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.30)', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Banknote size={14} color="#3DD194" style={{ flexShrink: 0 }} />
            <div>
              <span style={{ fontSize: 12, color: '#3DD194' }}>Estimated in your bank: </span>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#3DD194' }}>{payoutLandsLabel}</span>
            </div>
          </div>
          <p style={{ fontSize: 11, color: '#9AA4B2', margin: '4px 0 0 22px', lineHeight: 1.4 }}>
            Next bank run after release: {bankRunLabel}.
          </p>
        </div>
      </div>

      <p style={{ fontSize: 11, color: '#9AA4B2', margin: 0, lineHeight: 1.4 }}>
        Why two dates? The <strong>release date</strong> is when funds are released once the buyer
        confirms{courierTimerOff ? '' : ' (or auto-release fires)'}. They're then paid out on the next scheduled bank run, and
        the EFT clears into your account the following business day — that's the <strong>bank date</strong>.
        Estimates based on standard processing; actual timing may vary. This is not paid until bank settlement is confirmed.
      </p>
    </div>
  );
}



function TransactionDetail() {
  const [user, setUser] = useState(null);
  const [profileIncompleteError, setProfileIncompleteError] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [sellerConfirming, setSellerConfirming] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [reviewSkipped, setReviewSkipped] = useState(false);
  const [creatingEscrow, setCreatingEscrow] = useState(false);
  const [loadingPaymentLink, setLoadingPaymentLink] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [startingDelivery, setStartingDelivery] = useState(false);
  const [dispatchedLocally, setDispatchedLocally] = useState(false);
  const [acceptingDelivery, setAcceptingDelivery] = useState(false);
  const [deliveryConfirmedLocally, setDeliveryConfirmedLocally] = useState(false);
  const [payoutReadiness, setPayoutReadiness] = useState(null);
  const [checkingPayoutReadiness, setCheckingPayoutReadiness] = useState(false);
  const [fullCostQuote, setFullCostQuote] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [payConfirm, setPayConfirm] = useState(null);  // { link, total_value, processing_fee }
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  // True when a payment attempt finished without confirmation (cancelled at the
  // gateway, or 30s elapsed with no clearance) — we drop back to the payment
  // options and show a "please try again" hint instead of spinning forever.
  const [paymentNotConfirmed, setPaymentNotConfirmed] = useState(false);
  const [needsPhoneVerification, setNeedsPhoneVerification] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationError, setVerificationError] = useState(null);
  const [remainingOtpRequests, setRemainingOtpRequests] = useState(3);
  const [remainingVerifyAttempts, setRemainingVerifyAttempts] = useState(5);
  const [otpExpiresIn, setOtpExpiresIn] = useState(10);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutMinutes, setLockoutMinutes] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [wrongAccount, setWrongAccount] = useState(null);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [phoneVerificationContext, setPhoneVerificationContext] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const { config: platformConfig } = usePlatformConfig();
  const payoutSchedule = useMemo(() => getPayoutScheduleMessage(new Date(), platformConfig), [platformConfig]);
  // Shown after a release has just been confirmed — see getPostReleaseMessage
  // for why this must not reuse payoutSchedule's "Next payout release" copy.
  const postReleaseMessage = useMemo(() => getPostReleaseMessage(platformConfig), [platformConfig]);
  const navigate = useNavigate();
  const { transactionId } = useParams();

  useEffect(() => {
    // Smart Deal records (SD-*) live in the Smart Deals view, not the regular
    // escrow transaction page — redirect instead of calling /transactions/SD-*
    // (which is not a normal transaction). Strip any -M{n} milestone-child suffix.
    if (transactionId && transactionId.startsWith('SD-')) {
      const parentDealId = transactionId.replace(/-M\d+$/, '');
      navigate(`/smart-deals/${parentDealId}`, { replace: true });
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  useEffect(() => {
    if (!transaction || !user) return;
    const isBuyerUser = transaction.buyer_email === user.email || transaction.buyer_user_id === user.user_id;
    const escrowState = transaction.tradesafe_state;
    const hasEscrow = !!transaction.tradesafe_id;
    const uiState = resolveEscrowUiState(transaction);
    const canRelease = uiState.actionable && hasEscrow && isBuyerUser && ['INITIATED', 'SENT', 'DELIVERED'].includes(escrowState);
    if (canRelease && !payoutReadiness) checkPayoutReadiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction, user]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Is the transaction still waiting for the buyer's payment to be confirmed?
  const isAwaitingPayment = (txn) => {
    if (!txn) return false;
    const ps = String(txn.payment_status || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
    const ts = (txn.tradesafe_state || '').toUpperCase();
    return ['AWAITING_PAYMENT', 'PAYMENT_PENDING', 'READY_FOR_PAYMENT'].includes(ps) || ts === 'CREATED' || ts === 'PENDING';
  };

  // Bug 3: auto-refresh the transaction status every 15s without a full page reload.
  // While the payment is still awaiting confirmation we use the active TradeSafe
  // sync endpoint so both buyer and seller see the status flip as soon as funds
  // arrive, instead of waiting up to 30 minutes for the webhook.
  useEffect(() => {
    if (!transaction) return;
    if (resolveEscrowUiState(transaction).terminal) return;
    if (paymentProcessing) return; // handled by the faster post-payment poll below
    const interval = setInterval(async () => {
      if (isAwaitingPayment(transaction) && transaction.tradesafe_id) {
        try { await api.post(`${API}/tradesafe/sync/${transactionId}`, {}, { withCredentials: true }); } catch (e) {}
      }
      fetchData();
    }, 15000);
    return () => clearInterval(interval);
    // Polling is intentionally keyed to the provider-state scalars below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.delivery_confirmed, transaction?.payment_status, transaction?.tradesafe_state, transaction?.tradesafe_id, transactionId, paymentProcessing]);

  // Bug 2: if the buyer just initiated payment, enter "processing" mode on return.
  useEffect(() => {
    const flag = localStorage.getItem(`tt_payment_initiated_${transactionId}`);
    if (!flag) return;
    const ts = parseInt(flag, 10);
    if (isNaN(ts) || Date.now() - ts > 30 * 60 * 1000) {
      localStorage.removeItem(`tt_payment_initiated_${transactionId}`);
      return;
    }
    setPaymentProcessing(true);
  }, [transactionId]);

  // While processing, actively poll TradeSafe every ~8s so the status flips as soon
  // as funds clear. CRITICAL: never spin forever — after 30s with no confirmation we
  // drop back to the payment options with a "please try again" message, so a buyer
  // who cancelled at the gateway can immediately retry.
  useEffect(() => {
    if (!paymentProcessing) return;
    // If the transaction is already past awaiting payment, we're done.
    if (transaction && !isAwaitingPayment(transaction)) {
      setPaymentProcessing(false);
      setPaymentNotConfirmed(false);
      localStorage.removeItem(`tt_payment_initiated_${transactionId}`);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        if (transaction?.tradesafe_id) {
          await api.post(`${API}/tradesafe/sync/${transactionId}`, {}, { withCredentials: true });
        }
      } catch (e) {}
      if (!cancelled) await fetchData();
    };
    poll();
    const interval = setInterval(poll, 8000);
    const timeout = setTimeout(() => {
      if (cancelled) return;
      clearInterval(interval);
      setPaymentProcessing(false);
      setPaymentNotConfirmed(true);
      localStorage.removeItem(`tt_payment_initiated_${transactionId}`);
    }, 30000);
    return () => { cancelled = true; clearInterval(interval); clearTimeout(timeout); };
    // Polling is intentionally keyed to the provider-state scalars below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentProcessing, transaction?.tradesafe_id, transaction?.payment_status, transaction?.tradesafe_state, transactionId]);

  // Refresh immediately when user returns to tab after completing payment
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) fetchData(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchData);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchData);
    };
  }, [transactionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const userRes = await api.get(`${API}/auth/me`, { withCredentials: true });
      setUser(userRes.data);
      if (userRes.data.phone) setPhoneNumber(userRes.data.phone);
      const transactionRes = await api.get(`${API}/transactions/${transactionId}`, { withCredentials: true });
      let loadedTransaction = transactionRes.data;
      if (loadedTransaction.tradesafe_id && isProviderFundingConfirmed(loadedTransaction)) {
        try {
          const summaryRes = await api.get(`${API}/tradesafe/payment-summary/${transactionId}`, { withCredentials: true });
          if (summaryRes.data?.available) loadedTransaction = { ...loadedTransaction, ...summaryRes.data };
        } catch (summaryError) {
          // Exact provider amounts are optional; the transaction must still load
          // if TradeSafe is temporarily unavailable.
        }
      }
      setTransaction(loadedTransaction);
      setNeedsPhoneVerification(false);
      setPhoneVerificationContext(null);
      setLoadError(null);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorStatus = error.response?.status;
      if (errorStatus === 403 && typeof errorDetail === 'object' && errorDetail?.type === 'phone_verification_required') {
        setNeedsPhoneVerification(true);
        setPhoneVerificationContext({ maskedPhone: errorDetail.invited_phone_masked, inviteType: errorDetail.invite_type, itemDescription: errorDetail.item_description, itemPrice: errorDetail.item_price, message: errorDetail.message });
        setVerificationError(errorDetail.message);
        try { const userRes = await api.get(`${API}/auth/me`, { withCredentials: true }); setUser(userRes.data); if (userRes.data.phone) setPhoneNumber(userRes.data.phone); } catch (e) {}
      } else if (errorStatus === 403 && typeof errorDetail === 'string' && (errorDetail.includes('phone') || errorDetail.includes('Phone'))) {
        setNeedsPhoneVerification(true); setVerificationError(errorDetail);
        try { const userRes = await api.get(`${API}/auth/me`, { withCredentials: true }); setUser(userRes.data); if (userRes.data.phone) setPhoneNumber(userRes.data.phone); } catch (e) {}
      } else if (errorStatus === 403) {
        const errorMsg = typeof errorDetail === 'string' ? errorDetail : (errorDetail?.message || 'Access denied');
        const match = errorMsg.match(/sent to (?:email address |phone number )?([^\s.]+)/i);
        const expectedEmail = match ? match[1] : 'the invited account';
        try { const userRes = await api.get(`${API}/auth/me`, { withCredentials: true }); setUser(userRes.data); setWrongAccount({ expected: expectedEmail, current: userRes.data.email, message: errorMsg }); } catch (e) { setWrongAccount({ expected: expectedEmail, current: 'current account', message: errorMsg }); }
      } else if (errorStatus === 404) {
        // Only a genuine 404 means the transaction truly doesn't exist —
        // everything else (401 session blip, 500, timeout, network error,
        // especially right after returning from the payment provider) is
        // transient and must not destroy the user's place on this page.
        toast.error('Transaction not found');
        navigate('/transactions');
      } else if (!transaction) {
        // No transaction loaded yet and this wasn't a real 404 — keep the
        // user here with a retryable error instead of guessing it doesn't
        // exist. fetchData() is already re-invoked on focus/visibility and by
        // the polling effect below, so this also self-heals once the
        // transient condition clears.
        setLoadError('Could not load this transaction right now. This can happen right after returning from a payment — it will keep retrying.');
      }
      // If we already have a transaction loaded, a transient refetch failure
      // (e.g. a focus-triggered refresh hitting a brief backend hiccup) must
      // not clear it — just leave the existing data on screen.
    } finally { setLoading(false); }
  };

  const validatePhoneAgainstMask = (enteredPhone, maskedPhone) => {
    if (!maskedPhone) return true;
    let normalized = enteredPhone.replace(/[\s\-\+]/g, '');
    if (normalized.startsWith('0')) normalized = normalized.slice(1);
    if (normalized.startsWith('27')) normalized = normalized.slice(2);
    if (normalized.length < 9) return false;
    const match = maskedPhone.match(/(\d{4})$/);
    if (match) return normalized.endsWith(match[1]);
    return true;
  };

  const handleSendOtp = async () => {
    setVerificationError(null);
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 9) { toast.error('Please enter a valid phone number'); return; }
    if (isLockedOut) { toast.error(`Too many attempts. Please try again in ${lockoutMinutes} minutes.`); return; }
    const maskedPhone = phoneVerificationContext?.maskedPhone;
    if (maskedPhone && !validatePhoneAgainstMask(phoneNumber, maskedPhone)) { setVerificationError(`Phone number doesn't match. Expected ending: ${maskedPhone.slice(-4)}.`); toast.error(`Phone number doesn't match. Expected ending: ${maskedPhone.slice(-4)}`); return; }
    setSendingOtp(true);
    try {
      const response = await api.post(`${API}/verification/phone/send-otp`, { phone_number: phoneNumber, expected_phone_masked: maskedPhone || null }, { withCredentials: true });
      setOtpSent(true); setResendCooldown(response.data.cooldown_seconds || 60); setRemainingOtpRequests(response.data.remaining_requests ?? 2); setOtpExpiresIn(response.data.expires_in_minutes || 10); setRemainingVerifyAttempts(5);
      toast.success(`Verification code sent! Expires in ${response.data.expires_in_minutes || 10} minutes.`);
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Failed to send verification code';
      if (error.response?.status === 429) { if (errorDetail.includes('locked') || errorDetail.includes('Too many failed')) { setIsLockedOut(true); const minutes = errorDetail.match(/(\d+) minutes/); setLockoutMinutes(minutes ? parseInt(minutes[1]) : 30); } toast.error(errorDetail); }
      else if (errorDetail.includes("doesn't match")) { setVerificationError(errorDetail); toast.error('Phone number mismatch'); }
      else toast.error(errorDetail);
    } finally { setSendingOtp(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) { toast.error('Please enter the 6-digit code'); return; }
    if (isLockedOut) { toast.error(`Too many attempts. Please try again in ${lockoutMinutes} minutes.`); return; }
    setVerifyingOtp(true); setVerificationError(null);
    try {
      await api.post(`${API}/verification/phone/verify-otp`, { phone_number: phoneNumber, otp: otpCode }, { withCredentials: true });
      toast.success('Phone verified successfully!');
      const transactionRes = await api.get(`${API}/transactions/${transactionId}`, { withCredentials: true });
      setTransaction(transactionRes.data); setNeedsPhoneVerification(false); setVerificationError(null); setIsLockedOut(false);
      const userRes = await api.get(`${API}/auth/me`, { withCredentials: true }); setUser(userRes.data);
      toast.success('You have joined the transaction!');
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Verification failed';
      if (error.response?.status === 429) { setIsLockedOut(true); const minutes = errorDetail.match(/(\d+) minutes/); setLockoutMinutes(minutes ? parseInt(minutes[1]) : 30); setVerificationError(errorDetail); toast.error(errorDetail); }
      else if (errorDetail.includes('expired')) { setOtpSent(false); setOtpCode(''); setVerificationError('Verification code expired. Please request a new code.'); toast.error('Code expired. Request a new one.'); }
      else if (errorDetail.includes('attempts remaining')) { const remaining = errorDetail.match(/(\d+) attempts/); if (remaining) setRemainingVerifyAttempts(parseInt(remaining[1])); setVerificationError(errorDetail); toast.error(errorDetail); }
      else if (errorDetail.includes('No verification code')) { setOtpSent(false); setOtpCode(''); toast.error('Please request a new verification code.'); }
      else { setVerificationError(errorDetail); toast.error(errorDetail); }
    } finally { setVerifyingOtp(false); }
  };

  const handleSellerConfirm = async () => {
    if (!window.confirm('Are you sure you want to confirm these transaction details?')) return;
    setSellerConfirming(true);
    try {
      const res = await api.post(`${API}/transactions/${transactionId}/seller-confirm`, { confirmed: true }, { withCredentials: true });
      toast.success('Transaction confirmed!'); fetchData();
    } catch (error) {
      const errMsg = parseErrorMessage(error);
      if (errMsg && errMsg.includes(BANKING_DETAILS_PROMPT)) {
        setProfileIncompleteError(BANKING_DETAILS_PROMPT);
        toast.error(BANKING_DETAILS_PROMPT);
      } else if (errMsg && errMsg.includes(PHONE_VERIFICATION_PROMPT)) {
        setProfileIncompleteError(PHONE_VERIFICATION_PROMPT);
        toast.error(PHONE_VERIFICATION_PROMPT);
      } else if (errMsg && errMsg.startsWith('MISSING_PROFILE:')) {
        const missing = errMsg.replace('MISSING_PROFILE: ', '');
        setProfileIncompleteError(missing);
        toast.error(`Complete your profile first: ${missing}`);
      } else { toast.error(errMsg || 'Failed to confirm transaction'); }
    } finally { setSellerConfirming(false); }
  };

  const handleBuyerConfirm = async () => {
    if (!window.confirm('Are you sure you want to confirm these transaction details?')) return;
    setConfirming(true);
    try { await api.post(`${API}/transactions/${transactionId}/buyer-confirm`, { confirmed: true }, { withCredentials: true }); toast.success('Transaction confirmed!'); fetchData(); }
    catch (error) { toast.error(parseErrorMessage(error) || 'Failed to confirm transaction'); }
    finally { setConfirming(false); }
  };

  const handleConfirmDelivery = async () => {
    if (!user?.phone_verified) {
      toast.info(PHONE_VERIFICATION_PROMPT);
      navigate('/verify/phone');
      return;
    }
    if (!window.confirm('Are you sure you want to confirm delivery and release the funds to the seller?')) return;
    setConfirming(true);
    try {
      await api.patch(`${API}/transactions/${transactionId}/delivery`, { delivery_confirmed: true }, { withCredentials: true });
      setDeliveryConfirmedLocally(true);
      setTransaction(prev => prev ? { ...prev, delivery_confirmed: true, payment_status: 'Payment processing' } : prev);
      toast.success(postReleaseMessage.copy);
      fetchData();
    }
    catch (error) { if (error.response?.data?.detail === 'EMAIL_NOT_VERIFIED') { setEmailVerificationRequired(true); } else { toast.error(parseErrorMessage(error) || 'Failed to confirm delivery'); } }
    finally { setConfirming(false); }
  };

  const handleConfirmInstantRelease = async () => {
    if (!user?.phone_verified) {
      toast.info(PHONE_VERIFICATION_PROMPT);
      navigate('/verify/phone');
      return;
    }
    const readiness = payoutReadiness?.payout_ready ? payoutReadiness : await checkPayoutReadiness();
    if (readiness && !readiness.payout_ready) {
      toast.warning(readiness.issues?.join(', ') || 'Seller payout setup incomplete');
      return;
    }
    if (!window.confirm(`Confirm you are satisfied and want to release the funds to the seller? ${payoutSchedule.copy} This action cannot be undone.`)) return;
    setAcceptingDelivery(true);
    try {
      // Single merged buyer action: confirm the transaction details first (if the
      // buyer hasn't already — e.g. Smart Deals fund escrow without a separate
      // confirm step), then release the funds in the same click.
      if (!buyerConfirmed) {
        await api.post(`${API}/transactions/${transactionId}/buyer-confirm`, { confirmed: true }, { withCredentials: true });
      }
      await api.post(`${API}/tradesafe/release-instant/${transactionId}`, {}, { withCredentials: true });
      setDeliveryConfirmedLocally(true);
      setTransaction(prev => prev ? { ...prev, buyer_confirmed: true, delivery_confirmed: true, payment_status: 'Payment processing' } : prev);
      toast.success(postReleaseMessage.copy);
      fetchData();
    }
    catch (error) { if (error.response?.data?.detail === 'EMAIL_NOT_VERIFIED') { setEmailVerificationRequired(true); } else { toast.error(parseErrorMessage(error) || 'Failed to release funds'); } }
    finally { setAcceptingDelivery(false); }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`${API}/transactions/${transactionId}/agreement-pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `TrustTrade_Agreement_${transactionId}.pdf`); document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
      toast.success('Agreement downloaded');
    } catch (error) { toast.error('Agreement not available yet'); }
  };

  const handleSyncStatus = async () => {
    if (!transaction.tradesafe_id) { toast.info('No payment linked to sync'); fetchData(); return; }
    setSyncing(true);
    try {
      const response = await api.post(`${API}/tradesafe/sync/${transactionId}`, {}, { withCredentials: true });
      if (response.data.state_changed) toast.success(`Status updated: ${response.data.new_payment_status}`); else toast.info('Status is up to date');
      // Re-fetch the latest record and decide whether payment is confirmed yet.
      const txnRes = await api.get(`${API}/transactions/${transactionId}`, { withCredentials: true });
      setTransaction(txnRes.data);
      // Still awaiting payment (e.g. the buyer cancelled at the gateway, state is
      // still CREATED) → stop the processing spinner and show the payment options
      // again so they can retry immediately.
      if (isAwaitingPayment(txnRes.data)) {
        setPaymentProcessing(false);
        setPaymentNotConfirmed(true);
        localStorage.removeItem(`tt_payment_initiated_${transactionId}`);
      } else {
        setPaymentNotConfirmed(false);
      }
    } catch (error) { toast.error(parseErrorMessage(error) || 'Failed to sync status'); fetchData(); }
    finally { setSyncing(false); }
  };

  const handleCreateEscrow = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!user?.phone_verified) {
      toast.info(PHONE_VERIFICATION_PROMPT);
      navigate('/verify/phone');
      return;
    }
    if (!window.confirm('This will set up the protected payment. The buyer will then need to make payment. Proceed?')) return;
    setCreatingEscrow(true); toast.info('Setting up protected payment...');
    try {
      await api.post(`${API}/tradesafe/create-transaction`, { transaction_id: transactionId, fee_allocation: transaction.fee_allocation || 'SELLER_AGENT' }, { withCredentials: true });
      toast.success('Protected payment created! Buyer can now make payment.'); fetchData();
    } catch (error) { const errorMessage = error.response?.data?.detail ? parseErrorMessage(error) : 'Failed to start the transaction. Please try again.'; toast.error(errorMessage); }
    finally { setCreatingEscrow(false); }
  };

  const handleGetPaymentLink = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!user?.phone_verified) {
      toast.info(PHONE_VERIFICATION_PROMPT);
      navigate('/verify/phone');
      return;
    }
    if (!selectedPaymentMethod) { toast.error('Please select a payment method first'); return; }
    if (selectedPaymentMethod !== 'eft' && !payableMethods(fullCostQuote).includes(String(selectedPaymentMethod).toUpperCase())) {
      toast.error('The full cost for this payment method is not available yet. Refresh the quote before paying.');
      return;
    }
    setPaymentNotConfirmed(false);
    if (selectedPaymentMethod === 'eft') {
      setLoadingPaymentLink(true); toast.info('Loading payment details...');
      try {
        const response = await api.get(`${API}/tradesafe/payment-url/${transactionId}?payment_method=eft`, { withCredentials: true });
        setPaymentInfo(response.data);
        if (response.data.eft_details) { toast.info('EFT bank details ready — see below.'); }
      } catch (error) { toast.error(error.response?.data?.detail ? parseErrorMessage(error) : 'Unable to process payment. Please try again.'); }
      finally { setLoadingPaymentLink(false); }
      return;
    }
    localStorage.setItem('lastPaymentTransactionRoute', `/transactions/${transactionId}`);
    sessionStorage.setItem('redirectAfterLogin', `/transactions/${transactionId}`);
    navigate(`/pay?tx=${encodeURIComponent(transactionId)}&method=${encodeURIComponent(selectedPaymentMethod)}`);
  };

  // Confirmed in the payment modal — redirect to the secure gateway.
  const confirmAndPay = () => {
    const pc = payConfirm;
    if (!pc) return;
    setPayConfirm(null);
    setPaymentNotConfirmed(false);
    localStorage.setItem(`tt_payment_initiated_${transactionId}`, String(Date.now()));
    setPaymentProcessing(true);
    const w = window.open(pc.link, '_blank');
    if (!w || w.closed || typeof w.closed === 'undefined') window.location.href = pc.link;
    toast.success('Secure payment page opened.');
  };

  const handleStartDelivery = async () => {
    if (!user?.phone_verified) {
      toast.info(PHONE_VERIFICATION_PROMPT);
      navigate('/verify/phone');
      return;
    }
    if (!window.confirm('Mark this item as dispatched/delivered?')) return;
    setStartingDelivery(true);
    try {
      await api.post(`${API}/tradesafe/start-delivery/${transactionId}`, {}, { withCredentials: true });
      setDispatchedLocally(true);
      setTransaction(prev => prev ? { ...prev, tradesafe_state: 'INITIATED', payment_status: 'Delivery in Progress', delivery_started_at: new Date().toISOString() } : prev);
      toast.success('Delivery marked — buyer has been notified.');
      await fetchData();
    }
    catch (error) { toast.error(parseErrorMessage(error) || 'Failed to start delivery.'); }
    finally { setStartingDelivery(false); }
  };

  const handleManualStartDelivery = async () => {
    if (!user?.phone_verified) {
      toast.info(PHONE_VERIFICATION_PROMPT);
      navigate('/verify/phone');
      return;
    }
    if (!window.confirm('MANUAL OVERRIDE: Mark as dispatched?')) return;
    setStartingDelivery(true);
    try {
      await api.post(`${API}/tradesafe/manual-start-delivery/${transactionId}`, {}, { withCredentials: true });
      setDispatchedLocally(true);
      setTransaction(prev => prev ? { ...prev, tradesafe_state: 'INITIATED', payment_status: 'Delivery in Progress', delivery_started_at: new Date().toISOString() } : prev);
      toast.success('Delivery marked — buyer has been notified.');
      await fetchData();
    }
    catch (error) { toast.error(parseErrorMessage(error) || 'Failed to start delivery.'); }
    finally { setStartingDelivery(false); }
  };

  const checkPayoutReadiness = async () => {
    if (!transactionId) return;
    setCheckingPayoutReadiness(true);
    try { const response = await api.get(`${API}/tradesafe/payout-readiness/${transactionId}`, { withCredentials: true }); setPayoutReadiness(response.data); return response.data; }
    catch (error) { const fallback = { payout_ready: null, issues: ['Could not verify payout readiness'] }; setPayoutReadiness(fallback); return fallback; }
    finally { setCheckingPayoutReadiness(false); }
  };

  const handleAcceptDelivery = async () => {
    if (!user?.phone_verified) {
      toast.info(PHONE_VERIFICATION_PROMPT);
      navigate('/verify/phone');
      return;
    }
    const readiness = payoutReadiness?.payout_ready ? payoutReadiness : await checkPayoutReadiness();
    if (readiness && !readiness.payout_ready) {
      const issues = readiness.issues?.join(', ') || 'Unknown issue';
      toast.warning(issues);
      return;
    }
    if (!window.confirm(`Confirm you have received the item? This will release funds to the seller. ${payoutSchedule.copy} This action cannot be undone.`)) {
      return;
    }
    setAcceptingDelivery(true);
    try {
      await api.post(`${API}/tradesafe/accept-delivery/${transactionId}`, {}, { withCredentials: true });
      setDeliveryConfirmedLocally(true);
      setTransaction(prev => prev ? { ...prev, delivery_confirmed: true, payment_status: 'Payment processing' } : prev);
      toast.success(postReleaseMessage.copy);
      fetchData();
    }
    catch (error) {
      console.error('[CONFIRM_RECEIPT] accept-delivery FAILED', { status: error.response?.status, detail: error.response?.data?.detail, error });
      if (error.response?.data?.detail === 'EMAIL_NOT_VERIFIED') { setEmailVerificationRequired(true); } else { toast.error(parseErrorMessage(error) || 'Failed to confirm delivery.'); }
    }
    finally { setAcceptingDelivery(false); }
  };

  const handleManualAcceptDelivery = async () => {
    if (!user?.phone_verified) {
      toast.info(PHONE_VERIFICATION_PROMPT);
      navigate('/verify/phone');
      return;
    }
    const readiness = payoutReadiness?.payout_ready ? payoutReadiness : await checkPayoutReadiness();
    if (readiness && !readiness.payout_ready) {
      const issues = readiness.issues?.join(', ') || 'Unknown issue';
      toast.warning(issues);
      return;
    }
    if (!window.confirm(`MANUAL OVERRIDE: Confirm receipt and release funds to seller? ${payoutSchedule.copy}`)) return;
    setAcceptingDelivery(true);
    try { await api.post(`${API}/tradesafe/manual-accept-delivery/${transactionId}`, {}, { withCredentials: true }); toast.success(postReleaseMessage.copy); fetchData(); }
    catch (error) { if (error.response?.data?.detail === 'EMAIL_NOT_VERIFIED') { setEmailVerificationRequired(true); } else { toast.error(parseErrorMessage(error) || 'Failed to confirm delivery.'); } }
    finally { setAcceptingDelivery(false); }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) { toast.error('Please select a rating'); return; }
    setSubmittingRating(true);
    try { await api.post(`${API}/transactions/${transactionId}/rate`, { rating, review: review.trim() || null }, { withCredentials: true }); toast.success('Rating submitted!'); fetchData(); }
    catch (error) { toast.error(parseErrorMessage(error) || 'Failed to submit rating'); }
    finally { setSubmittingRating(false); }
  };

  // Skip the review prompt — recorded server-side so it never shows again.
  const handleSkipReview = async () => {
    setReviewSkipped(true);
    try { await api.post(`${API}/transactions/${transactionId}/review`, { skipped: true }, { withCredentials: true }); }
    catch (e) { /* non-blocking: the prompt is already hidden for this session */ }
  };

  const StarRating = ({ value, onSelect, onHover, readOnly = false, size = 'w-8 h-8' }) => (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1,2,3,4,5].map((star) => (
        <button key={star} type="button" disabled={readOnly}
          onClick={() => !readOnly && onSelect && onSelect(star)}
          onMouseEnter={() => !readOnly && onHover && onHover(star)}
          onMouseLeave={() => !readOnly && onHover && onHover(0)}
          data-testid={`star-${star}`}
          style={{ background: 'none', border: 'none', cursor: readOnly ? 'default' : 'pointer', padding: 2, transition: 'transform 0.1s', transform: 'scale(1)' }}
        >
          <Star style={{ width: 24, height: 24, fill: star <= (readOnly ? value : (hoverRating || value)) ? '#fbbf24' : 'none', color: star <= (readOnly ? value : (hoverRating || value)) ? '#fbbf24' : '#242830' }} />
        </button>
      ))}
    </div>
  );

  const getEscrowStateBadge = (state) => {
    const variants = { 'CREATED': { bg: '#242830', text: '#9AA4B2', label: 'Created' }, 'PENDING': { bg: 'rgba(245,158,11,0.14)', text: '#F5AB1F', label: 'Pending' }, 'FUNDS_RECEIVED': { bg: 'rgba(16,185,129,0.14)', text: '#3DD194', label: 'Payment Protected' }, 'FUNDS_DEPOSITED': { bg: 'rgba(16,185,129,0.14)', text: '#3DD194', label: 'Payment Protected' }, 'INITIATED': { bg: 'rgba(139,92,246,0.14)', text: '#C4B5FD', label: 'Delivery Started' }, 'SENT': { bg: 'rgba(59,130,246,0.14)', text: '#2F9BFF', label: 'Item Sent' }, 'DELIVERED': { bg: 'rgba(245,158,11,0.14)', text: '#F5AB1F', label: 'Awaiting Confirmation' }, 'FUNDS_RELEASED': { bg: 'rgba(16,185,129,0.14)', text: '#3DD194', label: 'Funds Released' }, 'EXPIRED': { bg: '#242830', text: '#9AA4B2', label: 'Expired' }, 'DISPUTED': { bg: 'rgba(239,68,68,0.14)', text: '#F0665C', label: 'Under Review' }, 'CANCELLED': { bg: 'rgba(239,68,68,0.14)', text: '#F0665C', label: 'Cancelled' } };
    return variants[state] || { bg: '#242830', text: '#9AA4B2', label: state };
  };

  const mapPaymentStatusToState = (paymentStatus, tradesafeState) => {
    const ps = String(paymentStatus || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
    const ts = String(tradesafeState || '').trim().toUpperCase();
    if (ps === 'EXPIRED' || ts === 'EXPIRED') return 'EXPIRED';
    if (ts === 'FUNDS_RELEASED') return 'FUNDS_RELEASED';
    if (ts === 'DELIVERED' || ps === 'DELIVERED') return 'DELIVERED';
    if (['INITIATED', 'SENT'].includes(ts) || ['DELIVERY_IN_PROGRESS', 'DISPATCHED'].includes(ps)) return 'DELIVERY_IN_PROGRESS';
    if (['FUNDS_RECEIVED', 'FUNDS_DEPOSITED'].includes(ts) || ['FUNDS_SECURED', 'PAID'].includes(ps)) return 'PAYMENT_SECURED';
    if (['AWAITING_PAYMENT', 'PAYMENT_PENDING'].includes(ps) || ['CREATED', 'PENDING'].includes(ts)) return 'AWAITING_PAYMENT';
    if (['PENDING', 'PENDING_CONFIRMATION'].includes(ps)) return 'PENDING_CONFIRMATION';
    if (ps === 'DISPUTED' || ts === 'DISPUTED') return 'DISPUTED';
    if (ps === 'CANCELLED' || ts === 'CANCELLED') return 'CANCELLED';
    if (ps === 'REFUNDED' || ts === 'REFUNDED') return 'REFUNDED';
    return 'CREATED';
  };

  const getFeePayerLabel = (feeAllocation) => {
    if (!feeAllocation) return 'Seller pays fee';
    switch(feeAllocation.toUpperCase()) {
      case 'BUYER_AGENT': case 'BUYER': return 'Buyer pays fee';
      case 'SELLER_AGENT': case 'SELLER': return 'Seller pays fee';
      case 'SPLIT_AGENT': case 'BUYER_SELLER_AGENT': case 'SPLIT': return 'Fee split 50/50';
      default: return 'Seller pays fee';
    }
  };

  // ── Shared styles ──────────────────────────────────────────────────
  const S = {
    card: { background: '#16181B', borderRadius: 14, border: '1px solid #242830', boxShadow: D.shadow, overflow: 'hidden' },
    label: { fontSize: 11, fontWeight: 600, color: '#9AA4B2', textTransform: 'uppercase', letterSpacing: '0.06em' },
    sectionTitle: { fontSize: 15, fontWeight: 600, color: '#F5F7FA', margin: 0 },
    divider: { height: 1, background: '#242830', margin: '12px 0' },
    pill: (bg, color) => ({ display: 'inline-block', fontSize: 11, fontWeight: 500, padding: '2px 9px', borderRadius: 20, background: bg, color }),
    actionCard: (accent, bg) => ({
      background: bg, border: `1px solid ${accent}33`, borderLeft: `3px solid ${accent}`,
      borderRadius: 14, padding: '20px 22px',
      boxShadow: D.shadow,
    }),
    btn: (bg, color = '#16181B') => ({
      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 20px',
      borderRadius: 9, border: 'none', background: bg, color, fontSize: 13, fontWeight: 600,
      cursor: 'pointer', transition: 'opacity 0.15s', whiteSpace: 'nowrap',
    }),
    btnOutline: { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9, border: '1px solid #242830', background: '#16181B', color: '#9AA4B2', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
    infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #242830' },
  };

  // ── Loading ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: D.bg }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 36, height: 36, border: '3px solid #3DD194', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ fontSize: 13, color: D.sub }}>Loading transaction…</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  // ── Transient load failure (not a real 404) ──────────────────────────
  // See fetchData's catch block: this is not "transaction not found", just a
  // recoverable blip (common right after returning from the payment
  // provider). Give the user a retry instead of bouncing them away and
  // losing their place.
  if (loadError && !transaction) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: D.bg, padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <p style={{ fontSize: 14, color: D.sub, marginBottom: 16 }}>{loadError}</p>
          <button
            type="button"
            onClick={() => { setLoading(true); fetchData(); }}
            style={{ background: '#3DD194', color: '#061007', border: 'none', borderRadius: 9, padding: '10px 18px', fontWeight: 700, cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Phone verification ──────────────────────────────────────────────
  if (needsPhoneVerification) {
    return (
      <DashboardLayout user={user} darkMain>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ maxWidth: 440, margin: '40px auto', padding: '0 16px' }}>
          <div style={{ ...S.card, padding: '36px 32px' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(59,130,246,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Phone size={24} color="#2F9BFF" />
              </div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: '#F5F7FA', margin: '0 0 8px' }}>Verify Your Phone</h1>
              <p style={{ fontSize: 13, color: '#9AA4B2', margin: 0 }}>This transaction was sent to a phone number. Verify to access it.</p>
            </div>

            {phoneVerificationContext && (
              <div style={{ background: '#101317', border: '1px solid #242830', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
                {phoneVerificationContext.itemDescription && <div style={S.infoRow}><span style={{ fontSize: 12, color: '#9AA4B2' }}>Item</span><span style={{ fontSize: 13, fontWeight: 500, color: '#F5F7FA' }}>{phoneVerificationContext.itemDescription}</span></div>}
                {phoneVerificationContext.itemPrice > 0 && <div style={{ ...S.infoRow, borderBottom: 'none' }}><span style={{ fontSize: 12, color: '#9AA4B2' }}>Amount</span><span style={{ fontSize: 14, fontWeight: 700, color: '#3DD194' }}>R {phoneVerificationContext.itemPrice.toFixed(2)}</span></div>}
                {phoneVerificationContext.maskedPhone && <div style={{ paddingTop: 10, borderTop: '1px solid #242830', display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 12, color: '#9AA4B2' }}>Sent to</span><span style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 600, color: '#2F9BFF' }}>{phoneVerificationContext.maskedPhone}</span></div>}
              </div>
            )}

            {verificationError && (
              <div style={{ background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.30)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 10 }}>
                <AlertTriangle size={15} color="#F0665C" style={{ flexShrink: 0, marginTop: 1 }} />
                <p style={{ fontSize: 13, color: '#F0665C', margin: 0 }}>{verificationError}</p>
              </div>
            )}
            {isLockedOut && (
              <div style={{ background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.30)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Lock size={15} color="#F0665C" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div><p style={{ fontSize: 13, fontWeight: 600, color: '#F0665C', margin: '0 0 3px' }}>Account Temporarily Locked</p><p style={{ fontSize: 12, color: '#F0665C', margin: 0 }}>Try again in {lockoutMinutes} minutes.</p></div>
                </div>
              </div>
            )}

            {!otpSent ? (
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#F5F7FA', marginBottom: 8 }}>Phone Number</label>
                <div style={{ position: 'relative', marginBottom: 16 }}>
                  <Phone size={15} color="#9AA4B2" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                  <Input type="tel" placeholder="+27 82 123 4567" value={phoneNumber} onChange={(e) => { setPhoneNumber(e.target.value); if (verificationError?.includes("doesn't match")) setVerificationError(null); }} style={{ paddingLeft: 36 }} data-testid="phone-input" disabled={isLockedOut} />
                </div>
                {phoneVerificationContext?.maskedPhone && <p style={{ fontSize: 12, color: '#2F9BFF', marginBottom: 16 }}>Enter the number matching: {phoneVerificationContext.maskedPhone}</p>}
                <button onClick={handleSendOtp} disabled={sendingOtp || !phoneNumber || isLockedOut} data-testid="send-otp-btn" style={{ ...S.btn('#2F9BFF'), width: '100%', justifyContent: 'center', opacity: (sendingOtp || !phoneNumber || isLockedOut) ? 0.5 : 1 }}>
                  {sendingOtp ? <><Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> Sending…</> : isLockedOut ? `Locked — Try in ${lockoutMinutes}m` : 'Send Verification Code'}
                </button>
              </div>
            ) : (
              <div>
                <div style={{ background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.30)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><CheckCircle2 size={14} color="#3DD194" /><span style={{ fontSize: 13, color: '#3DD194' }}>Code sent to <strong>{phoneNumber}</strong></span></div>
                  <span style={{ fontSize: 12, color: '#3DD194' }}>Expires {otpExpiresIn}m</span>
                </div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#F5F7FA', marginBottom: 8 }}>Verification Code</label>
                <Input type="text" placeholder="000000" value={otpCode} onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g,'').slice(0,6)); if (verificationError?.includes('attempts')) setVerificationError(null); }} style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.3em', fontFamily: 'monospace', marginBottom: 4 }} maxLength={6} data-testid="otp-input" disabled={isLockedOut} />
                <p style={{ fontSize: 12, color: '#9AA4B2', textAlign: 'center', marginBottom: 16 }}>Code expires in {otpExpiresIn} minutes</p>
                {remainingVerifyAttempts < 5 && <p style={{ fontSize: 12, color: '#F5AB1F', textAlign: 'center', marginBottom: 12 }}>{remainingVerifyAttempts} attempts remaining</p>}
                <button onClick={handleVerifyOtp} disabled={verifyingOtp || otpCode.length !== 6 || isLockedOut} data-testid="verify-otp-btn" style={{ ...S.btn('#3DD194'), width: '100%', justifyContent: 'center', opacity: (verifyingOtp || otpCode.length !== 6 || isLockedOut) ? 0.5 : 1, marginBottom: 12 }}>
                  {verifyingOtp ? <><Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> Verifying…</> : isLockedOut ? `Locked — Try in ${lockoutMinutes}m` : 'Verify & Join Transaction'}
                </button>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <button onClick={() => { setOtpSent(false); setOtpCode(''); setVerificationError(null); }} disabled={isLockedOut} style={{ background: 'none', border: 'none', color: '#9AA4B2', cursor: 'pointer' }}>Change number</button>
                  {resendCooldown > 0 ? <span style={{ color: '#9AA4B2' }}>Resend in {resendCooldown}s</span> : remainingOtpRequests > 0 && !isLockedOut ? <button onClick={handleSendOtp} disabled={sendingOtp} style={{ background: 'none', border: 'none', color: '#2F9BFF', fontWeight: 600, cursor: 'pointer' }}>Resend ({remainingOtpRequests} left)</button> : <span style={{ color: '#9AA4B2', fontSize: 12 }}>No more requests</span>}
                </div>
              </div>
            )}

            <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #242830' }}>
              <button onClick={() => navigate('/transactions')} style={{ ...S.btnOutline, width: '100%', justifyContent: 'center' }}>
                <ArrowLeft size={13} /> Back to My Transactions
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Wrong account ───────────────────────────────────────────────────
  if (wrongAccount) {
    const handleLogout = async () => {
      try { await api.post('/auth/logout', {}, { withCredentials: true }); localStorage.removeItem('session_token'); window.location.href = '/login'; }
      catch (error) { window.location.href = '/login'; }
    };
    return (
      <DashboardLayout user={user} darkMain>
        <div style={{ maxWidth: 440, margin: '40px auto', padding: '0 16px' }}>
          <div style={{ ...S.card, padding: '36px 32px', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(245,158,11,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
              <AlertTriangle size={24} color="#F5AB1F" />
            </div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#F5F7FA', margin: '0 0 8px' }}>Wrong Account</h1>
            <p style={{ fontSize: 13, color: '#9AA4B2', margin: '0 0 24px' }}>This transaction was sent to a different account.</p>
            <div style={{ background: '#101317', border: '1px solid #242830', borderRadius: 10, padding: '16px', textAlign: 'left', marginBottom: 20 }}>
              <div style={{ marginBottom: 12 }}><p style={{ ...S.label, marginBottom: 4 }}>Transaction sent to</p><p style={{ fontSize: 14, fontWeight: 600, color: '#F5F7FA', margin: 0 }}>{wrongAccount.expected}</p></div>
              <div style={{ paddingTop: 12, borderTop: '1px solid #242830' }}><p style={{ ...S.label, marginBottom: 4 }}>You are logged in as</p><p style={{ fontSize: 14, fontWeight: 600, color: '#F5F7FA', margin: 0 }}>{wrongAccount.current}</p></div>
            </div>
            <button onClick={handleLogout} style={{ ...S.btn('#2F9BFF'), width: '100%', justifyContent: 'center', marginBottom: 10 }}>Log Out and Switch Account</button>
            <button onClick={() => navigate('/transactions')} style={{ ...S.btnOutline, width: '100%', justifyContent: 'center' }}>Continue as {wrongAccount.current?.split('@')[0]}</button>
            <p style={{ fontSize: 12, color: '#9AA4B2', marginTop: 14 }}>Log out and sign in with {wrongAccount.expected} to view this transaction.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!transaction) return null;

  // ── Computed flags ──────────────────────────────────────────────────
  const isBuyer = user?.user_id === transaction.buyer_user_id || (user?.email && transaction.buyer_email && user.email.toLowerCase() === transaction.buyer_email.toLowerCase());
  const isSeller = user?.user_id === transaction.seller_user_id || (user?.email && transaction.seller_email && user.email.toLowerCase() === transaction.seller_email.toLowerCase());

  // True when the seller can already receive payouts — don't nag them to "add banking
  // details". Accept any reliable signal: the backend's validated payout flag, the
  // completion flag, or a saved profile account (bank name + account number).
  const sellerHasBankingDetails = Boolean(
    user?.banking_account_valid ||
    user?.banking_details_completed ||
    (user?.banking_details?.bank_name && user?.banking_details?.account_number)
  );

  const hasEscrow = !!transaction.tradesafe_id;
  const escrowState = transaction.tradesafe_state;
  const buyerConfirmed = transaction.buyer_confirmed;
  const sellerConfirmed = transaction.seller_confirmed;
  const bothConfirmed = buyerConfirmed && sellerConfirmed;
  const transactionDisputes = [
    ...(Array.isArray(transaction.disputes) ? transaction.disputes : []),
    ...(transaction.dispute ? [transaction.dispute] : []),
  ];
  const uiState = resolveEscrowUiState(transaction, transactionDisputes, { role: isSeller ? 'seller' : 'buyer' });
  const flowType = getTransactionFlowType(transaction);
  const activityEvents = buildTransactionActivity(transaction, {
    user,
    disputes: transactionDisputes,
    includeUpcoming: true,
    chronological: true,
  });
  const isDeliveryFlow = flowType === 'delivery';
  const isInstantFlow = flowType === 'instant';
  const isInPersonFlow = isInPersonTransaction(transaction);
  const courierActionsEnabled = COURIER_ENABLED || (transaction.delivery_method || 'courier').trim().toLowerCase() !== 'courier';
  const isFinalized = uiState.state === 'COMPLETED';
  // deliveryConfirmedLocally is only ever set true by the three buyer-side
  // confirm handlers in THIS browser session (an optimistic UI update for the
  // person who just clicked confirm). It is never derived from the fetched
  // transaction, so the OTHER party's session (typically the seller) — who
  // never calls those handlers — stays stuck showing pre-confirmation copy
  // forever, even after a refresh re-fetches the real transaction. Reading
  // transaction.delivery_confirmed here is the durable, server-sourced signal
  // both parties' sessions agree on.
  const handoverConfirmed = deliveryConfirmedLocally || Boolean(transaction.delivery_confirmed);
  // One-time review prompt: show the rating form only until the user rates or skips.
  const reviewDismissed = reviewSkipped || (transaction.review_dismissed_by || []).includes(user?.user_id);
  const myRating = isBuyer ? transaction.buyer_rating : isSeller ? transaction.seller_rating : null;
  const counterpartyRating = isBuyer ? transaction.seller_rating : isSeller ? transaction.buyer_rating : null;
  const showReviewForm = isFinalized && (isBuyer || isSeller) && !myRating && !reviewDismissed;
  const showRatingCard = isFinalized && (myRating || counterpartyRating || showReviewForm);
  const isActionable = uiState.actionable && !uiState.terminal;
  const canBuyerConfirm = isActionable && isBuyer && !buyerConfirmed;
  const canSellerConfirm = isActionable && isSeller && !sellerConfirmed;
  const canFundEscrowSetup = isActionable && isBuyer && bothConfirmed && !hasEscrow && transaction.item_price >= 100;
  const sellerWaitingForBuyerFunding = isActionable && isSeller && bothConfirmed && !hasEscrow;
  const canMakePayment = isActionable && hasEscrow && isBuyer && !isSeller && bothConfirmed && (escrowState === 'CREATED' || escrowState === 'PENDING' || transaction.payment_status === 'Awaiting Payment');
  const isAwaitingBuyerPayment = isActionable && hasEscrow && isSeller && !isBuyer && (escrowState === 'CREATED' || escrowState === 'PENDING' || transaction.payment_status === 'Awaiting Payment');
  // FUNDS_RECEIVED is intermediate — TradeSafe rejects allocationStartDelivery until
  // the allocation fully settles to FUNDS_DEPOSITED, so only offer the action then.
  const {
    canStartDelivery,
    paymentStillSettling,
    deliveryMarkedStarted,
    canManualStartDelivery,
    canAcceptDeliveryTS,
    canManualAcceptDelivery,
    canConfirmInstantRelease,
    buyerWaitingForSellerDispatch,
  } = getTransactionActionAvailability(transaction, { isBuyer, isSeller, isActionable });
  const canConfirmDelivery = isActionable && isDeliveryFlow && !hasEscrow && isBuyer && !transaction.delivery_confirmed && transaction.payment_status === 'Paid';
  const showPayoutTracker = ['RELEASED', 'PAYOUT_REVIEW'].includes(uiState.state);
  const shareLink = publicTransactionUrl(transaction.share_token || transaction.share_code);
  const _fa = (transaction.fee_allocation || 'BUYER').toUpperCase();
  // Launch fee rule: courier delivery is a pass-through buyer cost, and the
  // TrustTrade fee is calculated on item value only.
  const courierDeliveryFee = Number(transaction.courier_fee || 0);
  const _courierTotal = courierDeliveryFee;
  const _ttFee = roundMoney(resolveTransactionPlatformFee(transaction, platformConfig.platform_fee_percent));
  const { buyerFee: _buyerFee, sellerFee: _sellerFee } = splitTrustTradeFee(_ttFee, _fa);
  // Whether EACH side carries any provider processing cost under the current
  // allocation — a BUYER allocation guarantees the seller their full
  // item_price, not just their full item_price minus TradeSafe's cut.
  const buyerHasProviderFee = tsFeeApplies(_fa, 'buyer');
  const sellerHasProviderFee = tsFeeApplies(_fa, 'seller');
  const estimatedSecurePayment = roundMoney(Number(transaction.item_price || 0) + _courierTotal + _buyerFee);
  // A provider-fee estimate is only shown under the conditions the observed
  // checkout evidence covers: buyer pays every fee and no courier
  // pass-through is in the provider amount. Anything else falls back to
  // "Exact charges confirmed at secure checkout".
  const providerEstimateEligible = ['BUYER', 'BUYER_AGENT'].includes(_fa) && _courierTotal === 0;
  const paymentAmounts = getPaymentDisplayAmounts(transaction, {
    buyerPays: estimatedSecurePayment,
    processingCost: 0,
    trustTradeFee: _ttFee,
  });
  const totalSecurePayment = paymentAmounts.buyerPays;
  const displayedProcessingCost = paymentAmounts.processingCost;
  // "Up to" is a true ceiling: the provider's own processing fee (unknown
  // until payout) can only reduce this further, never computed as a guess here.
  const _sellerPayoutDisplay = getSellerPayoutDisplayAmount(
    transaction,
    roundMoney(Number(transaction.item_price || 0) - _sellerFee),
  );
  const sellerReceivesAmount = _sellerPayoutDisplay.amount;
  const sellerPayoutIsEstimate = _sellerPayoutDisplay.isEstimate;
  const escrowBadge = getEscrowStateBadge(escrowState);
  const currentStatusLabel = uiState.label || escrowBadge.label || transaction.payment_status || 'Status pending';
  const currentStatusBg = uiState.bg || escrowBadge.bg || '#242830';
  const currentStatusColor = uiState.color || escrowBadge.text || '#9AA4B2';
  const shareMessage = shareLink
    ? `I've created a secure TrustTrade protected payment for you. Click the link to view and confirm the transaction: ${shareLink}`
    : '';
  const whatsappShareHref = shareLink ? `https://wa.me/?text=${encodeURIComponent(shareMessage)}` : '';
  const fundsSecured = isProviderFundingConfirmed(transaction);
  const buyerDisplayName = transaction.buyer_name && transaction.buyer_name !== 'Pending' ? transaction.buyer_name : 'The buyer';
  const waitingForBuyerPayment = sellerWaitingForBuyerFunding || isAwaitingBuyerPayment;
  const buyerCanPay = canFundEscrowSetup || canMakePayment;
  const reminderHref = shareLink ? `https://wa.me/?text=${encodeURIComponent(`Hi ${buyerDisplayName}, please complete payment for our TrustTrade deal: ${shareLink}`)}` : '';


  const handleRefreshTracking = async () => {
    if (!COURIER_ENABLED || !transaction?.courier_waybill) return;
    setTrackingLoading(true);
    try {
      const res = await api.get(`/courier/track/${transaction.courier_waybill}`);
      setTrackingData(res.data);
      toast.success('Tracking updated');
    } catch {
      toast.error('Could not fetch tracking info');
    } finally {
      setTrackingLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <DashboardLayout user={user} darkMain>
      <PaymentConfirmModal
        open={!!payConfirm}
        amount={payConfirm?.total_value}
        processingFee={payConfirm?.processing_fee}
        onConfirm={confirmAndPay}
        onCancel={() => setPayConfirm(null)}
      />
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        .td-tab{padding:9px 16px;border:none;background:transparent;font-size:13px;font-weight:500;color:#9AA4B2;cursor:pointer;border-bottom:2px solid transparent;transition:all 0.15s}
        .td-tab.active{color:#F5F7FA;border-bottom-color:#F5F7FA;font-weight:600}
        .td-tab:hover{color:#F5F7FA}
        .pm-opt{border:1.5px solid #242830;borderRadius:12px;padding:14px 16px;cursor:pointer;transition:all 0.15s;background:#16181B}
        .pm-opt:hover{border-color:#93c5fd}
        .pm-opt.selected{border-color:#2F9BFF;background:rgba(59,130,246,0.14);box-shadow:0 0 0 3px rgba(59,130,246,0.08)}
        .action-btn:hover{opacity:0.88}
        .action-btn:active{opacity:0.75}
        html, body, #root { overflow-x: hidden; }
        .transaction-detail-shell { max-width: 800px; width: 100%; min-width: 0; margin: 0 auto; }
        .transaction-detail-grid { display: block; min-width: 0; }
        .transaction-detail-main { min-width: 0; }
        .td-primary-actions > div { background: transparent !important; border: none !important; padding: 0 !important; margin-bottom: 0 !important; }
        .td-simple-progress { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); list-style: none; gap: 12px; margin: 0; padding: 8px 0; }
        .td-simple-progress li { display: flex; align-items: center; gap: 8px; min-width: 0; font-size: 14px; font-weight: 550; }
        .td-progress-marker { display: flex; align-items: center; justify-content: center; border: 1px solid; border-radius: 50%; width: 30px; height: 30px; flex-shrink: 0; font-size: 13px; }
        .td-deal-details { margin: 0; }
        .td-deal-details > div { display: grid; grid-template-columns: minmax(0,1fr) minmax(0,1.4fr); gap: 16px; padding: 9px 0; border-bottom: 1px solid #242830; font-size: 15px; line-height: 1.5; }
        .td-deal-details dt { color: #9AA4B2; }
        .td-deal-details dd { margin: 0; text-align: right; color: #F5F7FA; overflow-wrap: anywhere; }
        .td-full-details > summary { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; color: #9AA4B2; font-size: 15px; font-weight: 600; cursor: pointer; }
        .td-full-details > summary:hover { color: #F5F7FA; }
        .td-full-details > summary:focus-visible, .action-btn:focus-visible { outline: 2px solid #2F9BFF; outline-offset: 3px; }
        @media (max-width: 768px) {
          .transaction-detail-shell { padding-bottom: 100px; }
          .action-btn { width: 100% !important; justify-content: center !important; white-space: normal !important; }
          .td-status-card { padding: 20px !important; }
          .td-tab { padding: 12px 10px; font-size: 14px; }
          .td-simple-progress li { flex-direction: column; align-items: center; text-align: center; gap: 8px; }
          .td-parties-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 380px) {
          .td-simple-progress { gap: 6px; }
          .td-simple-progress li { font-size: 12px; gap: 5px; }
          .td-progress-marker { width: 26px; height: 26px; }
        }
      `}</style>

      <div className="transaction-detail-shell">
        {emailVerificationRequired && <EmailVerificationPrompt />}
        {/* Back */}
        <button onClick={() => navigate('/transactions')} data-testid="back-to-transactions-btn" style={{ ...S.btnOutline, marginBottom: 20 }}>
          <ArrowLeft size={13} /> Back
        </button>

        {/* Two-column */}
        <div className="transaction-detail-grid">

          {/* ── Left column ─────────────────────────────── */}
          <div className="transaction-detail-main" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <section className="td-status-card" data-testid="transaction-next-action" style={{ ...S.card, padding: '24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
              <TransactionStateSummary transaction={transaction} uiState={uiState} role={isBuyer ? 'buyer' : isSeller ? 'seller' : 'viewer'} paymentStillSettling={paymentStillSettling} paymentProcessing={paymentProcessing} preparingPayment={canFundEscrowSetup} />
              <div className="td-primary-actions" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Buyer confirm — hidden when the merged "Confirm & Release Payment"
                button is available (that single action confirms AND releases), so the
                buyer never sees two separate confirm buttons. */}
            {canBuyerConfirm && !canConfirmInstantRelease && (
              <div style={S.actionCard('#2F9BFF', 'rgba(59,130,246,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(59,130,246,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CheckCircle2 size={18} color="#2F9BFF" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#2F9BFF', margin: '0 0 4px' }}>Action Required: Confirm Details</p>
                    <p style={{ fontSize: 13, color: '#2F9BFF', margin: '0 0 14px' }}>Review the transaction details and confirm to proceed with protected payment.</p>
                    <button onClick={handleBuyerConfirm} disabled={confirming} data-testid="buyer-confirm-btn" className="action-btn" style={{ ...S.btn('#2F9BFF'), opacity: confirming ? 0.6 : 1 }}>
                      {confirming ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Confirming…</> : <><CheckCircle2 size={13} /> Confirm Transaction</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isBuyer && !user?.phone_verified && (canFundEscrowSetup || canMakePayment || canAcceptDeliveryTS || canManualAcceptDelivery || canConfirmInstantRelease) && (
              <div style={S.actionCard('#2F9BFF', 'rgba(59,130,246,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(59,130,246,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Shield size={18} color="#2F9BFF" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#2F9BFF', margin: '0 0 4px' }}>{PHONE_VERIFICATION_PROMPT}</p>
                    <p style={{ fontSize: 13, color: '#2F9BFF', margin: '0 0 14px' }}>Phone verification is required to continue with this transaction.</p>
                    <button onClick={() => navigate('/verify/phone')} className="action-btn" style={{ ...S.btn('#2F9BFF') }}>
                      <Shield size={13} /> Verify Phone
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isSeller && !sellerHasBankingDetails && (isFinalized || canStartDelivery || canManualStartDelivery || canAcceptDeliveryTS || canManualAcceptDelivery || canConfirmInstantRelease) && (
              <div style={S.actionCard('#F5AB1F', 'rgba(245,158,11,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(245,158,11,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Banknote size={18} color="#F5AB1F" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#F5AB1F', margin: '0 0 4px' }}>{BANKING_DETAILS_PROMPT}</p>
                    <p style={{ fontSize: 13, color: '#F5AB1F', margin: '0 0 14px' }}>Add your banking details before funds are released so payouts can complete cleanly.</p>
                    <button onClick={() => navigate('/settings/banking')} className="action-btn" style={{ ...S.btn('#F5AB1F') }}>
                      <Banknote size={13} /> Add banking details
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Seller confirm */}
            {canSellerConfirm && (
              <div style={S.actionCard('#f97316', 'rgba(245,158,11,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(245,158,11,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText size={18} color="#f97316" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#F5AB1F', margin: '0 0 4px' }}>Action Required: Confirm the agreement</p>
                    <p style={{ fontSize: 13, color: '#FB923C', margin: '0 0 4px' }}>
                      TrustTrade fee: 2% of the item price (minimum R5).{' '}
                      {['SELLER_AGENT', 'SELLER'].includes(_fa) ? 'The TrustTrade fee is deducted from your payout.' : ['BUYER_SELLER', 'SPLIT_AGENT', 'BUYER_SELLER_AGENT', 'SPLIT'].includes(_fa) ? 'The TrustTrade fee is split — half from buyer, half deducted from your payout.' : 'The TrustTrade fee is included in the buyer\'s payment. You receive the full item value.'} {BANKING_DETAILS_PROMPT}
                    </p>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#F5AB1F', margin: '0 0 14px' }}>
                      {sellerPayoutIsEstimate && sellerHasProviderFee
                        ? `You'll receive up to R ${sellerReceivesAmount.toFixed(2)}, minus the payment provider's processing fee — confirmed by our payment provider at payout.`
                        : `You'll receive R ${sellerReceivesAmount.toFixed(2)}`}
                    </p>
                    {profileIncompleteError && (
                      <div style={{ background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.30)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#F0665C', margin: '0 0 4px' }}>Complete your profile first</p>
                        <p style={{ fontSize: 12, color: '#F0665C', margin: '0 0 8px' }}>{profileIncompleteError}</p>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <Link to="/verify/phone" style={{ fontSize: 12, color: '#F0665C', fontWeight: 600 }}>Verify phone</Link>
                          <Link to="/settings/banking" style={{ fontSize: 12, color: '#F0665C', fontWeight: 600 }}>Add banking details</Link>
                        </div>
                      </div>
                    )}
                    <button onClick={handleSellerConfirm} disabled={sellerConfirming} data-testid="seller-confirm-btn" className="action-btn" style={{ ...S.btn('#f97316'), opacity: sellerConfirming ? 0.6 : 1 }}>
                      {sellerConfirming ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Confirming…</> : <><CheckCircle2 size={13} /> Confirm the agreement</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Prepare payment using the existing protected-payment handler. */}
            {canFundEscrowSetup && (
              <div>
                <div style={{ marginBottom: 14 }}><FullCostQuote transactionId={transaction.transaction_id} onQuote={setFullCostQuote} /></div>
                <button type="button" onClick={handleCreateEscrow} onTouchEnd={handleCreateEscrow} disabled={creatingEscrow || payableMethods(fullCostQuote).length === 0} data-testid="create-escrow-btn" className="action-btn" style={{ ...S.btn('#2F9BFF'), minHeight: 48, fontSize: 16, opacity: creatingEscrow ? 0.6 : 1 }}>
                  {creatingEscrow ? <><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Preparing payment…</> : <><Shield size={16} /> Pay securely</>}
                </button>
              </div>
            )}

            {waitingForBuyerPayment && shareLink && <TransactionShareActions shareCode={transaction.share_token || transaction.share_code} compact />}
            {waitingForBuyerPayment && shareLink && (
              <div className="tt-reminder-card"><a href={reminderHref} target="_blank" rel="noopener noreferrer" data-testid="remind-buyer-whatsapp-btn" className="tt-whatsapp-reminder"><Phone size={19} aria-hidden="true" /><span><strong>Remind buyer on WhatsApp</strong><small>Send the secure transaction link</small></span></a><p>Share the TrustTrade transaction link. Never ask the other party to pay outside TrustTrade.</p></div>
            )}

            {/* Make payment */}
            {/* Bug 2: buyer just paid — show processing state with active polling + manual refresh */}
            {paymentInfo?.eft_details && !fundsSecured && (
              <div style={{ ...S.card, padding: '22px 24px', marginBottom: 16, border: '1px solid rgba(59,130,246,0.30)' }} data-testid="eft-details-card">
                <p style={{ fontSize: 16, fontWeight: 700, color: '#F5F7FA', margin: '0 0 4px' }}>🏦 Pay via EFT bank transfer</p>
                <p style={{ fontSize: 13, color: '#9AA4B2', margin: '0 0 16px' }}>{paymentInfo.eft_details.instructions}</p>
                {[
                  ['Bank', paymentInfo.eft_details.bank],
                  ['Account name', paymentInfo.eft_details.account_name],
                  ['Account number', paymentInfo.eft_details.account_number],
                  ['Branch code', paymentInfo.eft_details.branch_code],
                  ['Reference', paymentInfo.eft_details.reference],
                  ['Amount to pay', `R ${Number(paymentInfo.eft_details.amount ?? totalSecurePayment).toFixed(2)}`],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #242830' }}>
                    <span style={{ fontSize: 12, color: '#9AA4B2' }}>{label}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#F5F7FA', fontFamily: 'ui-monospace, monospace' }}>{value || '—'}</span>
                      {value && <button type="button" onClick={() => { navigator.clipboard.writeText(String(value)); toast.success('Copied'); }} style={{ fontSize: 11, color: '#2F9BFF', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Copy</button>}
                    </span>
                  </div>
                ))}
                <p style={{ fontSize: 12, color: '#F5AB1F', background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.30)', borderRadius: 8, padding: '10px 12px', marginTop: 14 }}>
                  Use the reference <strong>exactly as shown</strong>. Your transaction stays in <strong>Awaiting Payment</strong> until funds are confirmed (1–2 business days).
                </p>
              </div>
            )}

            {paymentProcessing && (
              <div style={S.actionCard('#2F9BFF', 'rgba(59,130,246,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(59,130,246,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Loader2 size={18} color="#2F9BFF" style={{ animation: 'spin 0.8s linear infinite' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#2F9BFF', margin: '0 0 4px' }}>Payment processing…</p>
                    <p style={{ fontSize: 13, color: '#2F9BFF', margin: '0 0 14px' }}>Your payment is being confirmed, this usually takes less than a minute. If you cancelled, refresh to choose a payment method again.</p>
                    <button type="button" onClick={handleSyncStatus} disabled={syncing} data-testid="refresh-status-btn" className="action-btn" style={{ ...S.btn('#2F9BFF'), opacity: syncing ? 0.6 : 1 }}>
                      {syncing ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Refreshing…</> : <><RefreshCw size={13} /> Refresh Status</>}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPaymentProcessing(false); setPaymentNotConfirmed(true); localStorage.removeItem(`tt_payment_initiated_${transactionId}`); }}
                      style={{ display: 'block', marginTop: 10, background: 'none', border: 'none', color: '#9AA4B2', fontSize: 12, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                    >
                      Cancelled payment? Choose a payment method again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {canMakePayment && !paymentProcessing && (
              <div style={{ ...S.card, padding: '22px 24px' }}>
                {paymentNotConfirmed && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.30)', borderRadius: 10, padding: '12px 14px', marginBottom: 18 }}>
                    <AlertTriangle size={16} color="#F5AB1F" style={{ flexShrink: 0, marginTop: 1 }} />
                    <p style={{ fontSize: 13, color: '#F5AB1F', margin: 0, lineHeight: 1.5 }}>
                      Payment not confirmed — please try again. Choose a payment method below to retry. Your money is only taken once payment completes.
                    </p>
                  </div>
                )}
                {/* Card vs Ozow cost comparison is the ONE payment-method selector.
                    Selecting either cost card updates selectedPaymentMethod and the
                    Pay button uses that choice. The item/TrustTrade-fee/courier
                    breakdown itself already lives in the shared MoneyBreakdown
                    component elsewhere on this page, so this section does not
                    duplicate the general transaction breakdown. Rate priority (see PaymentMethodFeeCompare /
                    paymentMethodFeeCompareCalc.js): an account-CONFIRMED rate
                    (platformConfig.payment_method_processing_fee_confirmed) if
                    one exists, else the payment provider's own
                    published-but-unverified DOCUMENTED rate
                    (payment_method_processing_rate_documented),
                    else "Confirmed at secure checkout" with no number at all.
                    Only the confirmed tier is ever shown without a
                    pending-verification caveat. */}
                <div style={{ marginBottom: 14 }}><FullCostQuote transactionId={transaction.transaction_id} onQuote={setFullCostQuote} /></div>
                <div style={{ marginBottom: 20 }}>
                  <PaymentMethodFeeCompare
                    unknownFeeCopy="Exact charges shown in the full cost above"
                    itemAmount={transaction.item_price}
                    trustTradeFee={_buyerFee}
                    trustTradeFeePercent={platformConfig.platform_fee_percent}
                    baseBeforeProvider={estimatedSecurePayment}
                    confirmedRates={platformConfig.payment_method_processing_fee_confirmed || {}}
                    documentedRates={platformConfig.payment_method_processing_rate_documented || {}}
                    estimateReconciled={platformConfig.payment_method_documented_estimate_reconciled || {}}
                    quickSettlementFee={platformConfig.provider_quick_settlement_fee}
                    estimateEligible={providerEstimateEligible}
                    vatPercent={platformConfig.south_africa_vat_percent}
                    selectedMethod={selectedPaymentMethod}
                    onSelect={setSelectedPaymentMethod}
                  />
                </div>

                <button type="button" onClick={handleGetPaymentLink} onTouchEnd={(e) => { e.preventDefault(); handleGetPaymentLink(e); }} disabled={loadingPaymentLink || !selectedPaymentMethod || !payableMethods(fullCostQuote).includes(String(selectedPaymentMethod).toUpperCase())} data-testid="make-payment-btn" className="action-btn" style={{ ...S.btn(selectedPaymentMethod ? '#2F9BFF' : '#9AA4B2'), width: '100%', justifyContent: 'center', fontSize: 15, padding: '13px 20px', opacity: (loadingPaymentLink || !selectedPaymentMethod) ? 0.7 : 1 }}>
                  {loadingPaymentLink ? <><Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading Payment Page...</> : selectedPaymentMethod ? <><CreditCard size={15} /> Pay securely</> : <><CreditCard size={15} style={{ opacity: 0.5 }} /> Select a payment method</>}
                </button>
                <p style={{ fontSize: 12, color: '#9AA4B2', textAlign: 'center', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  <Shield size={11} /> Payment protected through our payment provider.
                </p>
              </div>
            )}

            {/* Seller — digital/instant release: funds secured, nothing for the seller
                to do. Clearly reassure them and tell them when auto-release happens.
                Hidden while a dispute is open — "no action needed, auto-release on
                <date>" would contradict the dispute-frozen state above (release is
                blocked, not merely pending). */}
            {isSeller && isInstantFlow && fundsSecured && !isFinalized && !handoverConfirmed && uiState.state !== 'DISPUTED' && (() => {
              const autoReleaseDate = transaction.auto_release_at
                ? new Date(transaction.auto_release_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })
                : null;
              return (
                <div style={{ ...S.actionCard('#3DD194', 'rgba(16,185,129,0.14)'), padding: '20px 20px' }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(16,185,129,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Shield size={18} color="#3DD194" />
                    </div>
                    <div style={{ flex: 1 }}>
                      {isInPersonFlow && ['ESCROW_LOCKED', 'DELIVERY_PENDING'].includes(uiState.state) && shareLink && <a className="tt-handover-contact" data-testid="arrange-handover-link" href={`https://wa.me/?text=${encodeURIComponent(`Let's arrange our in-person handover. View the TrustTrade transaction: ${shareLink}`)}`} target="_blank" rel="noopener noreferrer"><Phone size={16} aria-hidden="true" /> Arrange handover on WhatsApp</a>}
                      <p style={{ fontSize: 15, fontWeight: 700, color: '#3DD194', margin: '0 0 6px' }}>The buyer's payment is secured!</p>
                      <p style={{ fontSize: 13, color: '#3DD194', margin: '0 0 8px', lineHeight: 1.6 }}>
                        {isInPersonFlow ? 'The buyer will confirm the handover' : 'The buyer will confirm digital completion'}{autoReleaseDate ? <> or funds auto-release on <strong>{autoReleaseDate}</strong></> : ' or funds auto-release after the inspection window'}. You'll receive an SMS and email when your payout is processed.
                      </p>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#3DD194', margin: 0 }}>{isInPersonFlow ? 'No payment action needed from you. Agree the handover with the buyer.' : 'No payment action needed from you. Complete the agreed digital delivery.'}</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Seller transaction complete success screen */}
            {isSeller && (isFinalized || handoverConfirmed) && (() => {
              const sellerAmount = sellerReceivesAmount;
              const fmtAmount = `R ${Number(sellerAmount).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              return (
                <div style={{ ...S.actionCard('#3DD194', 'rgba(16,185,129,0.14)'), padding: '20px 20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10 }}>
                    <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(16,185,129,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CheckCircle2 size={28} color="#3DD194" />
                    </div>
                    <p style={{ fontSize: 18, fontWeight: 700, color: '#3DD194', margin: 0 }}>{isFinalized ? 'Payout Complete' : 'Payout Processing'}</p>
                    <p style={{ fontSize: 14, color: '#3DD194', margin: 0 }}>
                      {isFinalized
                        ? <>Your bank payout of <strong>{fmtAmount}</strong> has been confirmed.</>
                        : <>Your payout of <strong>{fmtAmount}</strong> is being processed to your bank. Paid is shown only after bank settlement.</>}
                    </p>
                    <p style={{ fontSize: 13, color: '#3DD194', background: 'rgba(16,185,129,0.14)', borderRadius: 8, padding: '8px 14px', margin: 0 }}>{payoutSchedule.copy}</p>
                  </div>
                </div>
              );
            })()}

            {/* Seller delivery success state */}
            {!isFinalized && !handoverConfirmed && (deliveryMarkedStarted || dispatchedLocally) && (
              <div style={S.actionCard('#3DD194', 'rgba(16,185,129,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(16,185,129,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CheckCircle2 size={18} color="#3DD194" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <p style={{ fontSize: 14, fontWeight: 600, color: '#3DD194', margin: 0 }}>Dispatched ✅</p>
                    </div>
                    <p style={{ fontSize: 13, color: '#3DD194', margin: 0 }}>Delivery marked — buyer has been notified. Waiting for their confirmation.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment received but not yet fully settled — dispatch isn't available yet */}
            {paymentStillSettling && !dispatchedLocally && (
              <div style={S.actionCard('#F5AB1F', 'rgba(210,153,34,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(210,153,34,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Clock size={18} color="#F5AB1F" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#F5AB1F', margin: '0 0 4px' }}>Payment Settling</p>
                    <p style={{ fontSize: 13, color: '#F5AB1F', margin: 0 }}>Payment is still settling — you'll be able to mark this as dispatched once it's fully confirmed, usually within a day or two.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Seller start delivery */}
            {courierActionsEnabled && canStartDelivery && !dispatchedLocally && (
              <div style={S.actionCard('#8b5cf6', 'rgba(139,92,246,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(139,92,246,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Truck size={18} color="#C4B5FD" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#C4B5FD', margin: '0 0 4px' }}>Funds Secured — Deliver Item</p>
                    <p style={{ fontSize: 13, color: '#C4B5FD', margin: '0 0 14px' }}>Payment received and held securely. Deliver the item to the buyer and mark as dispatched.</p>
                    <button onClick={handleStartDelivery} disabled={startingDelivery} data-testid="start-delivery-btn" className="action-btn" style={{ ...S.btn('#8b5cf6'), opacity: startingDelivery ? 0.6 : 1 }}>
                      {startingDelivery ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Processing…</> : <><Truck size={13} /> Mark as Dispatched</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Manual start delivery */}
            {courierActionsEnabled && !canStartDelivery && canManualStartDelivery && !transaction.delivery_started_at && !dispatchedLocally && (
              <div style={S.actionCard('#F5AB1F', 'rgba(245,158,11,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(245,158,11,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Truck size={18} color="#F5AB1F" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#F5AB1F', margin: '0 0 4px' }}>Mark as Dispatched</p>
                    <p style={{ fontSize: 13, color: '#F5AB1F', margin: '0 0 4px' }}>Payment appears received. Click to mark as dispatched.</p>
                    <p style={{ fontSize: 12, color: '#F5AB1F', background: 'rgba(245,158,11,0.14)', padding: '6px 10px', borderRadius: 6, margin: '0 0 14px' }}><strong>Note:</strong> Use this if the normal flow isn't showing buttons correctly.</p>
                    <button onClick={handleManualStartDelivery} disabled={startingDelivery} data-testid="manual-start-delivery-btn" className="action-btn" style={{ ...S.btn('#F5AB1F'), opacity: startingDelivery ? 0.6 : 1 }}>
                      {startingDelivery ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Processing…</> : <><Truck size={13} /> Mark as Dispatched</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Buyer waiting for seller to dispatch */}
            {buyerWaitingForSellerDispatch && (() => {
              const dm = transaction.delivery_method;
              const heading = dm === 'bank_deposit'
                ? 'Waiting for seller to confirm handover'
                : dm === 'digital' || dm === 'instant' || dm === 'immediate'
                  ? 'Waiting for seller to deliver'
                  : 'Waiting for seller to dispatch';
              const body = dm === 'bank_deposit'
                ? 'Your payment is secured. The seller will confirm when the handover is arranged.'
                : dm === 'digital' || dm === 'instant' || dm === 'immediate'
                  ? 'Your payment is secured. The seller has been notified to deliver your item digitally.'
                  : 'Your payment is secured. The seller has been notified to dispatch your item.';
              const status = dm === 'bank_deposit'
                ? 'Awaiting seller handover confirmation...'
                : dm === 'digital' || dm === 'instant' || dm === 'immediate'
                  ? 'Awaiting digital delivery...'
                  : 'Awaiting seller dispatch...';
              return (
              <div style={S.actionCard('#F5AB1F', 'rgba(245,158,11,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(245,158,11,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Truck size={18} color="#F5AB1F" />
                  </div>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#F5AB1F', margin: '0 0 4px' }}>{heading}</p>
                    <p style={{ fontSize: 13, color: '#F5AB1F', margin: '0 0 10px' }}>{body}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#F5AB1F' }}>
                      <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> {status}
                    </div>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* Buyer accept delivery */}
            {canAcceptDeliveryTS && (
              <div style={S.actionCard('#3DD194', 'rgba(16,185,129,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(16,185,129,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CheckCircle2 size={18} color="#3DD194" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#3DD194', margin: '0 0 4px' }}>{isInPersonFlow ? 'Confirm handover' : 'Confirm receipt'}</p>
                    <p style={{ fontSize: 13, color: '#3DD194', margin: '0 0 4px' }}>{isInPersonFlow ? 'Check the item after the in-person handover. Confirm only when satisfied.' : 'Check the agreed delivery. Confirm only when satisfied.'}</p>
                    <p style={{ fontSize: 12, color: '#3DD194', background: 'rgba(16,185,129,0.14)', padding: '6px 10px', borderRadius: 6, margin: '0 0 14px' }}><strong>Important:</strong> Only confirm if satisfied. This cannot be undone.</p>
                    {payoutReadiness && !payoutReadiness.payout_ready && (
                      <div style={{ background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.30)', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#F5AB1F', margin: '0 0 3px' }}>Seller Payout Setup Incomplete</p>
                        <p style={{ fontSize: 12, color: '#F5AB1F', margin: 0 }}>{payoutReadiness.issues?.join('. ') || 'Seller must complete payout setup.'}</p>
                        {payoutReadiness.can_auto_sync && <p style={{ fontSize: 12, color: '#F5AB1F', margin: '4px 0 0' }}>The system will attempt to sync automatically when you confirm.</p>}
                      </div>
                    )}
                    {checkingPayoutReadiness && <p style={{ fontSize: 12, color: '#9AA4B2', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}><Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> Checking payout readiness…</p>}
                    <button onClick={handleAcceptDelivery} disabled={acceptingDelivery || checkingPayoutReadiness} data-testid="accept-delivery-btn" className="action-btn" style={{ ...S.btn('#3DD194'), opacity: (acceptingDelivery || checkingPayoutReadiness) ? 0.6 : 1 }}>
                      {acceptingDelivery ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Processing…</> : <><CheckCircle2 size={13} /> {isInPersonFlow ? 'Confirm handover' : 'Confirm receipt'}</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Manual accept delivery */}
            {!canAcceptDeliveryTS && canManualAcceptDelivery && !transaction.delivery_confirmed && (
              <div style={S.actionCard('#3DD194', 'rgba(16,185,129,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(16,185,129,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CheckCircle2 size={18} color="#3DD194" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#3DD194', margin: '0 0 4px' }}>{isInPersonFlow ? 'Confirm handover' : 'Confirm receipt'}</p>
                    <p style={{ fontSize: 13, color: '#3DD194', margin: '0 0 14px' }}>{payoutSchedule.copy}</p>
                    {payoutReadiness && !payoutReadiness.payout_ready && (
                      <div style={{ background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.30)', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
                        <p style={{ fontSize: 12, color: '#F5AB1F', margin: 0 }}>{payoutReadiness.issues?.join('. ') || 'Seller must complete payout setup.'}</p>
                      </div>
                    )}
                    <button onClick={handleManualAcceptDelivery} disabled={acceptingDelivery || checkingPayoutReadiness} data-testid="manual-accept-delivery-btn" className="action-btn" style={{ ...S.btn('#3DD194'), opacity: (acceptingDelivery || checkingPayoutReadiness) ? 0.6 : 1 }}>
                      {acceptingDelivery ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Processing…</> : <><CheckCircle2 size={13} /> {isInPersonFlow ? 'Confirm handover' : 'Confirm receipt'}</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Instant release — buyer confirms satisfaction and releases funds */}
            {canConfirmInstantRelease && (
              <div style={S.actionCard('#3DD194', 'rgba(16,185,129,0.14)')}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(16,185,129,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CheckCircle2 size={18} color="#3DD194" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: '#3DD194', margin: '0 0 4px' }}>{isInPersonFlow ? 'Confirm Handover & Release Payment' : 'Confirm Digital Completion & Release Payment'}</p>
                    <p style={{ fontSize: 13, color: '#3DD194', margin: '0 0 4px' }}>{isInPersonFlow ? 'Your payment is protected. Confirm only after the in-person handover is complete and you are satisfied.' : 'Your payment is protected. Confirm only after digital delivery is complete and you are satisfied.'}</p>
                    <p style={{ fontSize: 12, color: '#3DD194', background: 'rgba(16,185,129,0.14)', padding: '6px 10px', borderRadius: 6, margin: '0 0 14px' }}><strong>Important:</strong> Only confirm if you are satisfied. This cannot be undone.</p>
                    {payoutReadiness && !payoutReadiness.payout_ready && (
                      <div style={{ background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.30)', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#F5AB1F', margin: '0 0 3px' }}>Seller Payout Setup Incomplete</p>
                        <p style={{ fontSize: 12, color: '#F5AB1F', margin: 0 }}>{payoutReadiness.issues?.join('. ') || 'Seller must complete payout setup before funds can be released.'}</p>
                        {payoutReadiness.can_auto_sync && <p style={{ fontSize: 12, color: '#F5AB1F', margin: '4px 0 0' }}>The system will attempt to sync automatically when you confirm.</p>}
                      </div>
                    )}
                    {checkingPayoutReadiness && <p style={{ fontSize: 12, color: '#9AA4B2', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}><Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> Checking payout readiness…</p>}
                    <button onClick={handleConfirmInstantRelease} disabled={acceptingDelivery || checkingPayoutReadiness} data-testid="confirm-instant-release-btn" className="action-btn" style={{ ...S.btn('#3DD194'), opacity: (acceptingDelivery || checkingPayoutReadiness) ? 0.6 : 1 }}>
                      {acceptingDelivery ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Processing…</> : <><CheckCircle2 size={13} /> {isInPersonFlow ? 'Confirm Handover & Release' : 'Confirm Digital Completion & Release'}</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Legacy status hints */}
            {isActionable && !hasEscrow && transaction.payment_status === 'Paid' && !transaction.delivery_confirmed && (
              <div style={S.actionCard('#F5AB1F', 'rgba(245,158,11,0.14)')}>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#F5AB1F', margin: '0 0 4px' }}>Payment Received — Awaiting Delivery</p>
                <p style={{ fontSize: 13, color: '#F5AB1F', margin: 0 }}>{isSeller ? 'Deliver the item. Funds are released after buyer confirmation.' : 'Payment is protected. Confirm delivery once received.'}</p>
              </div>
            )}
            </div>
              <div className="tt-transaction-identity"><span>{transaction.item_description}</span><span>{isBuyer ? 'Seller' : 'Buyer'} · {isBuyer ? transaction.seller_name : transaction.buyer_name}</span><VerificationBadges profile={isBuyer ? transaction.seller_profile : transaction.buyer_profile} /></div>
            </section>
            {/* Payment breakdown stands alone directly under the hero — no
                competing status panel beside it (the old "What happens
                next" card duplicated the hero's own action/description and
                was removed; see commit history for the page-simplification
                pass). */}
            <MoneyBreakdown role={isSeller ? 'seller' : 'buyer'} itemValue={transaction.item_price} buyerFee={_buyerFee} sellerFee={_sellerFee} deliveryFee={courierDeliveryFee} beforeProvider={estimatedSecurePayment} providerFee={paymentAmounts.hasProviderActuals ? displayedProcessingCost : null} confirmedTotal={paymentAmounts.hasProviderActuals ? totalSecurePayment : null} sellerReceives={sellerReceivesAmount} sellerReceivesIsCeiling={sellerPayoutIsEstimate && sellerHasProviderFee} providerFeeCopy={isSeller ? 'Shown to the buyer on the payment screen before they pay' : 'Shown on the payment screen before you pay'} paymentConfirmed={fundsSecured} providerFeeSplit={paymentAmounts.hasProviderActuals ? providerFeeSplit(transaction, displayedProcessingCost, totalSecurePayment) : null} paymentDetails={paymentRecord(transaction)} />
            <TransactionJourney transaction={transaction} uiState={uiState} role={isSeller ? 'seller' : 'buyer'} paymentStillSettling={paymentStillSettling} />

            {/* Secondary / support content — AI check, confirmation status,
                release-timing detail and payout tracking. None of this is
                needed to understand "what's happening / what do I do /
                what will I pay / what will the seller receive / where are
                we" (already answered above); it supports a viewer who wants
                more than the guided summary. */}
            {/* TrustTrade AI Check — quick warning banner for non-low risk */}
            {!transaction.ai_fraud_analysis && transaction.risk_level && transaction.risk_level !== 'low' && (
              <div style={{ ...S.actionCard(transaction.risk_level === 'high' ? '#F0665C' : '#F5AB1F', transaction.risk_level === 'high' ? 'rgba(239,68,68,0.14)' : 'rgba(245,158,11,0.14)') }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <AlertTriangle size={16} color={transaction.risk_level === 'high' ? '#F0665C' : '#F5AB1F'} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 600, color: transaction.risk_level === 'high' ? '#F0665C' : '#F5AB1F', margin: '0 0 4px' }}>{transaction.risk_level === 'high' ? 'Proceed carefully' : 'Some things to check'}</p>
                    <p style={{ fontSize: 13, color: transaction.risk_level === 'high' ? '#F0665C' : '#F5AB1F', margin: 0 }}>Our AI spotted something worth a look. Take a moment to verify the other party before continuing.</p>
                  </div>
                </div>
              </div>
            )}

            {transaction.ai_fraud_analysis && (['medium', 'high'].includes(transaction.ai_fraud_analysis.risk_level) || ['medium', 'high'].includes(transaction.risk_level)) && (() => {
              const fa = transaction.ai_fraud_analysis;
              const riskPalette = {
                low:    { bg: 'rgba(16,185,129,0.14)', border: 'rgba(16,185,129,0.30)', heading: '#3DD194', badge: 'rgba(16,185,129,0.14)', badgeText: '#3DD194' },
                medium: { bg: 'rgba(245,158,11,0.14)', border: 'rgba(245,158,11,0.30)', heading: '#F5AB1F', badge: 'rgba(245,158,11,0.14)', badgeText: '#F5AB1F' },
                high:   { bg: 'rgba(239,68,68,0.14)', border: 'rgba(239,68,68,0.30)', heading: '#F0665C', badge: 'rgba(239,68,68,0.14)', badgeText: '#F0665C' },
              };
              const riskLabel = {
                low:    'TrustTrade AI Check: Looks good ✓',
                medium: 'TrustTrade AI Check: Some things to check',
                high:   'TrustTrade AI Check: Proceed carefully',
              };
              const riskLevel = [fa.risk_level, transaction.risk_level].includes('high') ? 'high' : 'medium';
              const pal = riskPalette[riskLevel] || riskPalette.medium;
              const label = riskLabel[riskLevel] || riskLabel.medium;
              return (
                <div style={{
                  background: pal.bg, border: `1px solid ${pal.border}`,
                  borderLeft: `3px solid ${pal.badgeText}`, borderRadius: 14, padding: '16px 20px',
                }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: pal.heading, margin: '0 0 8px' }}>{label}</p>
                  {fa.summary && <p style={{ fontSize: 12, color: pal.heading, margin: '0 0 6px', lineHeight: 1.5 }}>{fa.summary}</p>}
                  {fa.flags && fa.flags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                      <span style={{ fontSize: 11, color: pal.heading, fontWeight: 500, width: '100%', marginBottom: 2 }}>Things to note:</span>
                      {fa.flags.map((f, i) => (
                        <span key={i} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: pal.badge, color: pal.badgeText, fontWeight: 500 }}>{f}</span>
                      ))}
                    </div>
                  )}
                  <p style={{ fontSize: 11, color: '#9AA4B2', margin: '10px 0 0', lineHeight: 1.4 }}>
                    This is a guide, not a guarantee. Always use your own judgement.
                  </p>
                </div>
              );
            })()}

            {/* Confirmation status */}
            {!bothConfirmed && (
              <div style={{ ...S.card, padding: '18px 20px' }}>
                <p style={{ ...S.label, marginBottom: 12 }}>Confirmation Status</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10 }}>
                  {[{ name: transaction.buyer_name, role: 'Buyer', confirmed: buyerConfirmed, accent: '#2F9BFF', bg: 'rgba(59,130,246,0.14)' }, { name: transaction.seller_name, role: 'Seller', confirmed: sellerConfirmed, accent: '#f97316', bg: 'rgba(245,158,11,0.14)' }].map(p => (
                    <div key={p.role} style={{ padding: '12px 14px', borderRadius: 10, background: p.confirmed ? 'rgba(16,185,129,0.14)' : '#101317', border: `1px solid ${p.confirmed ? 'rgba(16,185,129,0.30)' : '#242830'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: p.confirmed ? 'rgba(16,185,129,0.14)' : p.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <User size={12} color={p.confirmed ? '#3DD194' : p.accent} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#9AA4B2' }}>{p.role}</span>
                      </div>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#F5F7FA', margin: '0 0 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                      <span style={{ ...S.pill(p.confirmed ? 'rgba(16,185,129,0.14)' : '#101317', p.confirmed ? '#3DD194' : '#F5AB1F'), fontSize: 10 }}>
                        {p.confirmed ? '✓ Confirmed' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pre-release inspection countdown — shows the buyer (and seller) exactly
                when payment auto-releases, in plain English, after dispatch. */}
            <AutoReleaseCountdown transaction={transaction} isBuyer={isBuyer} isSeller={isSeller} />

            {isFinalized && (
              <>
                <FinalizedEscrowState transaction={transaction} uiState={uiState} />
                {showPayoutTracker
                  ? <PayoutTimelineTracker transaction={transaction} platformConfig={platformConfig} />
                  : <PayoutTimeline transaction={transaction} />
                }
              </>
            )}

            {!isFinalized && showPayoutTracker && (
              <PayoutTimelineTracker transaction={transaction} platformConfig={platformConfig} />
            )}

            {/* Seller preview: as soon as funds are in escrow ("Funds Secured" /
                "Payment Secured"), show an estimated payout date so the seller
                knows when to expect their money. Hidden once the post-release
                timeline takes over (isFinalized or showPayoutTracker), and while
                a dispute is open — an estimated release date next to "no action
                needed from you" would contradict the dispute-frozen state above
                and imply the payout is still on track when it is not. */}
            {isSeller && fundsSecured && !isFinalized && !showPayoutTracker && uiState.state !== 'DISPUTED' && (
              <SellerExpectedPayoutCard transaction={transaction} platformConfig={platformConfig} />
            )}

            {/* ONE secondary disclosure for everything that isn't needed to
                understand "what's happening / what do I do / what will I
                pay / what will the seller receive / where are we" (already
                answered by the hero, payment breakdown and progress rail
                above): item/participant/delivery/reference, fees and
                totals, and the full party/agreement/timeline/photos tabs
                below. Collapsed by default. */}
            <details className="td-full-details" style={{ ...S.card, overflow: 'hidden' }}>
              <summary>
                <span>View deal details</span>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSyncStatus(); }}
                  disabled={syncing}
                  data-testid="refresh-transaction-btn"
                  className="td-deal-details-refresh"
                >
                  {syncing ? <><Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> Checking…</> : <><RefreshCw size={12} /> Refresh status</>}
                </button>
              </summary>
              <div style={{ padding: '0 24px 20px' }}>
                <dl className="td-deal-details">
                  {[
                    ['Item', transaction.item_description],
                    ['Item value', `R ${Number(transaction.item_price || 0).toFixed(2)}`],
                    [isSeller ? 'You receive' : 'Seller receives', !paymentAmounts.hasProviderActuals && sellerHasProviderFee ? `Up to R ${sellerReceivesAmount.toFixed(2)} (confirmed by provider at payout)` : `R ${sellerReceivesAmount.toFixed(2)}`],
                    [isBuyer ? 'Seller' : 'Buyer', isBuyer ? transaction.seller_name : transaction.buyer_name],
                    ['Delivery', isInPersonFlow ? 'In-person meeting' : transaction.delivery_method === 'digital' ? 'Digital delivery' : transaction.delivery_method === 'courier' ? 'Courier delivery' : transaction.delivery_method || 'As agreed'],
                    ['Reference', transaction.share_code || 'Pending'],
                  ].map(([label, value]) => (
                    <div key={label}><dt>{label}</dt><dd>{value || 'Awaiting details'}</dd></div>
                  ))}
                </dl>
                <h2 style={{ color: '#F5F7FA', fontSize: 17, margin: '16px 0 14px' }}>Fees and totals</h2>
                <dl className="td-deal-details">
                  {[
                    ['Item value', `R ${Number(transaction.item_price || 0).toFixed(2)}`],
                    ...(courierDeliveryFee > 0 ? [['Delivery fee', `R ${courierDeliveryFee.toFixed(2)}`]] : []),
                    ['Buyer TrustTrade fee', `R ${_buyerFee.toFixed(2)}`],
                    ['Seller TrustTrade fee', `R ${_sellerFee.toFixed(2)}`],
                    ...(paymentAmounts.hasProviderActuals
                      ? (displayedProcessingCost > 0 ? [['Confirmed processing cost', (() => { const split = providerFeeSplit(transaction, displayedProcessingCost, totalSecurePayment); return split ? `TradeSafe fee R ${split.feeExclVat.toFixed(2)} + VAT R ${split.vat.toFixed(2)} = R ${split.total.toFixed(2)}` : `R ${displayedProcessingCost.toFixed(2)}`; })()]] : [])
                      : (buyerHasProviderFee ? [['Buyer processing fee', fundsSecured ? 'Included in the provider-confirmed payment' : 'Shown on the payment screen before you pay']] : [])),
                    ...(!paymentAmounts.hasProviderActuals && sellerHasProviderFee ? [['Seller processing fee', 'Confirmed by provider at payout']] : []),
                    [paymentAmounts.hasProviderActuals ? 'Buyer pays total' : 'Total before provider fee', paymentAmounts.hasProviderActuals ? `R ${totalSecurePayment.toFixed(2)}` : `R ${(Number(transaction.item_price || 0) + _buyerFee + courierDeliveryFee).toFixed(2)}`],
                    ['Seller receives', !paymentAmounts.hasProviderActuals && sellerHasProviderFee ? `Up to R ${sellerReceivesAmount.toFixed(2)}` : `R ${sellerReceivesAmount.toFixed(2)}`],
                  ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
                </dl>
                {!paymentAmounts.hasProviderActuals && !fundsSecured && <p style={{ color: '#9AA4B2', fontSize: 14, lineHeight: 1.5 }}>The full breakdown, including TradeSafe’s processing fee and VAT, is shown on the payment screen before you pay.</p>}
                {shareLink && !waitingForBuyerPayment && <TransactionShareActions shareCode={transaction.share_token || transaction.share_code} />}
              </div>

            {/* Tabs */}
            <div style={S.card}>
              <div style={{ display: 'flex', borderBottom: '1px solid #242830', padding: '0 8px' }}>
                {['overview', 'agreement', 'timeline', 'photos'].map(tab => (
                  <button key={tab} className={`td-tab${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              <div style={{ padding: '20px 22px' }}>
                {/* Overview tab */}
                {activeTab === 'overview' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div className="td-parties-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      {[
                        { title: 'Buyer', icon: User, color: '#2F9BFF', bg: 'rgba(59,130,246,0.14)', name: transaction.buyer_name, email: transaction.buyer_email, phone: transaction.buyer_phone, confirmed: buyerConfirmed, trust: transaction.buyer_trust, profile: transaction.buyer_profile },
                        { title: 'Seller', icon: User, color: '#f97316', bg: 'rgba(245,158,11,0.14)', name: transaction.seller_name, email: transaction.seller_email, phone: transaction.seller_phone, confirmed: sellerConfirmed, trust: transaction.seller_trust, profile: transaction.seller_profile },
                      ].map(p => (
                        <div key={p.title} style={{ background: '#101317', borderRadius: 10, padding: '14px 16px', border: '1px solid #242830' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: p.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <p.icon size={13} color={p.color} />
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#F5F7FA' }}>{p.title}</span>
                            {p.confirmed && <CheckCircle2 size={13} color="#3DD194" style={{ marginLeft: 'auto' }} />}
                          </div>
                          <p style={{ fontSize: 14, fontWeight: 600, color: '#F5F7FA', margin: '0 0 4px' }}>{p.name}</p>
                          <VerificationBadges profile={p.profile} />
                          {p.trust && (
                            <p style={{ fontSize: 12, fontWeight: 600, color: '#F5F7FA', margin: '0 0 4px' }}
                               title="Trust score · completed trades · valid disputes">
                              {p.trust.trust_score} trust · {p.trust.total_trades} {p.trust.total_trades === 1 ? 'trade' : 'trades'} · {p.trust.disputes} {p.trust.disputes === 1 ? 'dispute' : 'disputes'}
                            </p>
                          )}
                          {p.email && <p style={{ fontSize: 12, color: '#9AA4B2', margin: 0 }}>{p.email}</p>}
                          {p.phone && <p style={{ fontSize: 12, color: '#9AA4B2', margin: '2px 0 0', fontFamily: 'monospace' }}>{p.phone}</p>}
                          {transaction.invite_type === 'phone' && !p.email && !p.phone && <p style={{ fontSize: 12, color: '#2F9BFF', margin: '2px 0 0' }}>Invited via phone</p>}
                        </div>
                      ))}
                    </div>

                    <div style={{ background: '#101317', borderRadius: 10, padding: '14px 16px', border: '1px solid #242830' }}>
                      <p style={{ ...S.label, marginBottom: 12 }}>Item Details</p>
                      <p style={{ fontSize: 14, color: '#F5F7FA', margin: '0 0 10px', lineHeight: 1.6 }}>{transaction.item_description}</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {transaction.item_condition && <span style={S.pill('#242830', '#9AA4B2')}>{transaction.item_condition}</span>}
                        {transaction.delivery_method && (
                          <span style={S.pill('rgba(59,130,246,0.14)', '#2F9BFF')}>
                            {transaction.delivery_method === 'courier' ? 'Courier' : transaction.delivery_method === 'bank_deposit' ? 'In-Person Meeting' : 'Digital'}
                          </span>
                        )}
                      </div>
                      {transaction.days_to_deliver != null && transaction.days_to_deliver !== '' && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #242830', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 13, color: '#9AA4B2' }}>Days to deliver</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#F5F7FA' }}>{transaction.days_to_deliver} {Number(transaction.days_to_deliver) === 1 ? 'day' : 'days'}</span>
                        </div>
                      )}
                      {transaction.known_issues && transaction.known_issues !== 'None' && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #242830' }}>
                          <p style={{ ...S.label, marginBottom: 4 }}>Known Issues</p>
                          <p style={{ fontSize: 13, color: '#9AA4B2', margin: 0 }}>{transaction.known_issues}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Agreement tab */}
                {activeTab === 'agreement' && (
                  <div>
                    <p style={{ ...S.sectionTitle, marginBottom: 14 }}>Transaction Agreement</p>
                    {sellerConfirmed ? (
                      <div>
                        <p style={{ fontSize: 13, color: '#9AA4B2', marginBottom: 14 }}>The transaction agreement has been generated and is available for download.</p>
                        <button onClick={handleDownloadPDF} data-testid="download-agreement-btn" style={{ ...S.btn('#2F9BFF'), display: 'inline-flex' }}>
                          <Download size={13} /> Download Agreement (PDF)
                        </button>
                      </div>
                    ) : (
                      <p style={{ fontSize: 13, color: '#9AA4B2' }}>Agreement will be available once the seller confirms the transaction details.</p>
                    )}
                  </div>
                )}

                {/* Timeline tab */}
                {activeTab === 'timeline' && (
                  <div>
                    <p style={{ ...S.sectionTitle, marginBottom: 20 }}>Transaction Progress</p>
                    <TransactionTimeline transaction={transaction} currentState={mapEscrowUiStateToTimelineState(uiState) || transaction.transaction_state || mapPaymentStatusToState(transaction.payment_status, transaction.tradesafe_state)} timeline={transaction.timeline} />
                    <div style={{ marginTop: 24 }}>
                      <p style={{ ...S.sectionTitle, marginBottom: 12 }}>Transaction Activity</p>
                      <TransactionActivityFeed events={activityEvents} />
                    </div>
                    {uiState.state === 'DELIVERED' && (
                      <div style={{ marginTop: 20, padding: '12px 16px', borderRadius: 8, backgroundColor: 'rgba(26,115,232,0.08)' }}>
                        <p style={{ margin: 0, fontSize: 13, color: '#2F9BFF', fontWeight: 500 }}>
                          {isDeliveryFlow
                            ? `Funds are released when the buyer confirms receipt. ${payoutSchedule.copy}`
                            : `Funds are released when the transaction conditions are met. ${payoutSchedule.copy}`}
                        </p>
                      </div>
                    )}
                    <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #242830' }}>
                      <p style={{ ...S.label, marginBottom: 12 }}>Event History</p>
                      <Timeline transaction={transaction} />
                    </div>
                  </div>
                )}

                {/* Photos tab */}
                {activeTab === 'photos' && (
                  <div>
                    <p style={{ ...S.sectionTitle, marginBottom: 16 }}>Item Photos</p>
                    {transaction.item_photos && transaction.item_photos.length > 0 ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                        {transaction.item_photos.map((photo, index) => (
                          <div key={index} style={{ borderRadius: 10, overflow: 'hidden', cursor: 'pointer', position: 'relative', aspectRatio: '1', background: '#242830' }}>
                            <SecureImage reference={photo} category="photos" alt={`Photo ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <ImageIcon size={36} color="#242830" style={{ marginBottom: 10 }} />
                        <p style={{ fontSize: 14, color: '#9AA4B2' }}>No photos uploaded</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            </details>

            {/* Legacy confirm delivery */}
            {canConfirmDelivery && (
              <div style={S.actionCard('#3DD194', 'rgba(16,185,129,0.14)')}>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#3DD194', margin: '0 0 6px' }}>Final Step: Confirm Delivery</p>
                <p style={{ fontSize: 13, color: '#3DD194', margin: '0 0 14px' }}>{`Have you received the item and are satisfied? Confirming releases funds to the seller. ${payoutSchedule.copy} This cannot be undone.`}</p>
                <button onClick={handleConfirmDelivery} disabled={confirming} data-testid="confirm-delivery-btn" className="action-btn" style={{ ...S.btn('#3DD194'), opacity: confirming ? 0.6 : 1 }}>
                  {confirming ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Processing…</> : 'Confirm Delivery'}
                </button>
              </div>
            )}

            {/* Rating */}
            {showRatingCard && (
              <div style={{ ...S.card, padding: '22px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <Star size={16} color="#fbbf24" fill="#fbbf24" />
                  <p style={S.sectionTitle}>Rate This Transaction</p>
                </div>
                {isBuyer && transaction.buyer_rating ? (
                  <div><p style={{ fontSize: 13, color: '#9AA4B2', marginBottom: 8 }}>Your rating for the seller:</p><StarRating value={transaction.buyer_rating} readOnly size="w-6 h-6" />{transaction.buyer_review && <p style={{ fontSize: 13, color: '#9AA4B2', fontStyle: 'italic', marginTop: 8 }}>"{transaction.buyer_review}"</p>}</div>
                ) : isSeller && transaction.seller_rating ? (
                  <div><p style={{ fontSize: 13, color: '#9AA4B2', marginBottom: 8 }}>Your rating for the buyer:</p><StarRating value={transaction.seller_rating} readOnly size="w-6 h-6" />{transaction.seller_review && <p style={{ fontSize: 13, color: '#9AA4B2', fontStyle: 'italic', marginTop: 8 }}>"{transaction.seller_review}"</p>}</div>
                ) : showReviewForm ? (
                  <div>
                    <p style={{ fontSize: 13, color: '#9AA4B2', marginBottom: 12 }}>{isBuyer ? 'Rate your experience with the seller:' : 'Rate your experience with the buyer:'}</p>
                    <div style={{ marginBottom: 14 }}><StarRating value={rating} onSelect={setRating} onHover={setHoverRating} /></div>
                    <label style={{ display: 'block', fontSize: 13, color: '#9AA4B2', marginBottom: 6 }}>Review (optional)</label>
                    <Textarea placeholder="Share your experience…" value={review} onChange={(e) => setReview(e.target.value)} rows={3} data-testid="review-textarea" style={{ marginBottom: 12 }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button onClick={handleSubmitRating} disabled={submittingRating || rating === 0} data-testid="submit-rating-btn" className="action-btn" style={{ ...S.btn('#2F9BFF'), opacity: (submittingRating || rating === 0) ? 0.5 : 1 }}>
                        {submittingRating ? 'Submitting…' : 'Submit Rating'}
                      </button>
                      <button onClick={handleSkipReview} data-testid="skip-rating-btn" style={{ background: 'transparent', border: 'none', color: '#9AA4B2', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: '8px 4px' }}>
                        Skip
                      </button>
                    </div>
                  </div>
                ) : null}
                {isBuyer && transaction.seller_rating && <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #242830' }}><p style={{ fontSize: 13, color: '#9AA4B2', marginBottom: 8 }}>Seller's rating for you:</p><StarRating value={transaction.seller_rating} readOnly size="w-5 h-5" />{transaction.seller_review && <p style={{ fontSize: 13, color: '#9AA4B2', fontStyle: 'italic', marginTop: 6 }}>"{transaction.seller_review}"</p>}</div>}
                {isSeller && transaction.buyer_rating && <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #242830' }}><p style={{ fontSize: 13, color: '#9AA4B2', marginBottom: 8 }}>Buyer's rating for you:</p><StarRating value={transaction.buyer_rating} readOnly size="w-5 h-5" />{transaction.buyer_review && <p style={{ fontSize: 13, color: '#9AA4B2', fontStyle: 'italic', marginTop: 6 }}>"{transaction.buyer_review}"</p>}</div>}
              </div>
            )}

            {/* Courier Tracking */}
            {(transaction.courier_waybill || transaction.courier_tracking_reference || safeExternalHttpUrl(transaction.courier_tracking_url) || transaction.courier_status) && (
              <div data-testid="courier-tracking" style={{ ...S.card, padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Truck size={15} color="#2F9BFF" />
                    <p style={S.sectionTitle}>Courier Tracking</p>
                  </div>
                  {COURIER_ENABLED && transaction.courier_waybill && <button
                    onClick={handleRefreshTracking}
                    disabled={trackingLoading}
                    data-testid="refresh-tracking-btn"
                    style={{ ...S.btnOutline, padding: '5px 12px', fontSize: 12 }}
                  >
                    {trackingLoading
                      ? <><Loader2 size={11} style={{ animation: 'spin 0.8s linear infinite' }} /> Refreshing…</>
                      : <><RefreshCw size={11} /> Refresh Tracking</>}
                  </button>}
                </div>

                {(transaction.courier_waybill || transaction.courier_tracking_reference) && <div style={{ background: '#101317', borderRadius: 8, padding: '8px 12px', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={S.label}>{transaction.courier_waybill ? 'Waybill' : 'Tracking reference'}</span>
                  <code style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 600, color: '#2F9BFF' }}>{transaction.courier_waybill || transaction.courier_tracking_reference}</code>
                </div>}

                {safeExternalHttpUrl(transaction.courier_tracking_url) && (
                  <a
                    href={safeExternalHttpUrl(transaction.courier_tracking_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#2F9BFF', textDecoration: 'none', marginBottom: 14 }}
                  >
                    <Truck size={12} /> Track on Courier Guy &rarr;
                  </a>
                )}

                {trackingData ? (
                  <>
                    <div style={{ marginBottom: 14 }}>
                      <p style={{ ...S.label, marginBottom: 6 }}>Current Status</p>
                      <span style={{ ...S.pill('rgba(59,130,246,0.14)', '#2F9BFF'), fontSize: 12, fontWeight: 600 }}>
                        {trackingData.status || 'In transit'}
                      </span>
                      {trackingData.timestamp && (
                        <p style={{ fontSize: 11, color: '#9AA4B2', marginTop: 6 }}>
                          Last update: {new Date(trackingData.timestamp).toLocaleString()}
                        </p>
                      )}
                    </div>

                    {trackingData.events && trackingData.events.length > 0 && (
                      <div>
                        <p style={{ ...S.label, marginBottom: 10 }}>History</p>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {trackingData.events.slice(0, 6).map((evt, i, arr) => (
                            <div key={i} style={{ display: 'flex', gap: 12 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 3 }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: i === 0 ? '#2F9BFF' : '#242830', flexShrink: 0 }} />
                                {i < arr.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 18, background: '#242830', margin: '3px 0' }} />}
                              </div>
                              <div style={{ paddingBottom: 10 }}>
                                <p style={{ fontSize: 13, color: '#F5F7FA', margin: '0 0 2px', fontWeight: i === 0 ? 600 : 400 }}>
                                  {evt.status || evt.description || 'Update'}
                                </p>
                                <p style={{ fontSize: 11, color: '#9AA4B2', margin: 0 }}>
                                  {evt.created_at || evt.timestamp ? new Date(evt.created_at || evt.timestamp).toLocaleString() : ''}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p style={{ fontSize: 13, color: '#9AA4B2', textAlign: 'center', padding: '10px 0 2px' }}>
                    {transaction.courier_status || (COURIER_ENABLED && transaction.courier_waybill
                      ? 'Click Refresh to load tracking information.'
                      : 'Saved tracking information.')}
                  </p>
                )}
              </div>
            )}

            {/* Raise dispute */}
            {isActionable && !transaction.delivery_confirmed && sellerConfirmed && (
              <div style={{ ...S.card, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ fontSize: 13, color: '#9AA4B2', margin: 0 }}>Having issues with this transaction?</p>
                <button onClick={() => navigate('/disputes-dashboard', { state: { transactionId: transaction.transaction_id } })} style={{ ...S.btnOutline, fontSize: 12 }}>
                  <FileText size={12} /> Raise Dispute
                </button>
              </div>
            )}
          </div>
          {/* END left column */}



        </div>
      </div>
    </DashboardLayout>
  );
}

export default TransactionDetail;
